/**
 * Elite Batch 4 Part 1 — Workflow Templates 248–272
 * Communication (248–267) + Data & Analytics (268–272)
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

// =============================================================================
// ELITE BATCH 4 PART 1 — TEMPLATES 248–272
// =============================================================================

export const ELITE_BATCH4_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 248. SLACK CHANNEL ARCHIVER (8 nodes)
  // ===================================================================

  "Slack Channel Archiver": {
    summary:
      "On a weekly schedule, pulls all Slack channels via API, computes inactivity duration, uses AI to classify channels as archive/review/keep, auto-archives stale channels, logs every action to Google Sheets, and posts an admin summary to Slack.",
    nodes: [
      trigger("Weekly Slack channel audit schedule", "schedule", {
        schedule: "Every Monday at 9:00 AM UTC",
        howItStarts:
          "Runs weekly to audit all Slack channels for inactivity and auto-archive stale ones",
        setup:
          "Set the cron schedule for channel auditing. Ensure the Slack bot token has channels:read, channels:write, and channels:history scopes.",
        inputFields: [],
      }),

      // 2. Pull all Slack channels
      n("2", "hitApi", "Slack API: List all channels", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://slack.com/api/conversations.list?types=public_channel&exclude_archived=true&limit=999",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "channels", customName: "channel_list", type: "string" },
            {
              name: "response_metadata.next_cursor",
              customName: "next_cursor",
              type: "string",
            },
          ],
        },
      }),

      // 3. Compute inactivity metrics per channel
      n("3", "runCode", "Compute channel inactivity metrics", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Parse channel list and compute days since last message for each channel
const channels = JSON.parse(inputs.channel_list || '[]');
const now = Math.floor(Date.now() / 1000);
const results = channels.map(ch => {
  const lastActivity = ch.updated || ch.created;
  const daysSinceActivity = Math.round((now - lastActivity) / 86400);
  const memberCount = ch.num_members || 0;
  return {
    id: ch.id,
    name: ch.name,
    days_inactive: daysSinceActivity,
    member_count: memberCount,
    purpose: ch.purpose?.value || '',
    created: ch.created,
  };
});
const totalChannels = results.length;
const staleChannels = results.filter(c => c.days_inactive > 90);
const staleCount = staleChannels.length;
return {
  channel_analysis: JSON.stringify(results),
  total_channels: totalChannels,
  stale_count: staleCount,
  stale_channel_data: JSON.stringify(staleChannels),
};`,
          inputVariables: [{ name: "channel_list", type: "string" }],
          outputVariables: [
            { name: "channel_analysis", type: "string" },
            { name: "total_channels", type: "number" },
            { name: "stale_count", type: "number" },
            { name: "stale_channel_data", type: "string" },
          ],
        },
      }),

      // 4. AI classification
      aiNode(
        "4",
        "AI classify channels: archive/review/keep",
        760,
        "You are a Slack workspace administrator. Analyze each channel and classify it as archive (no activity 90+ days, low members), review (some activity but declining), or keep (active). Consider channel purpose and member count. Return JSON: {classifications: [{id, name, action, reason, confidence}], archive_ids: [string], review_ids: [string], archive_count: number, review_count: number}",
        "Channels to classify:\\n{stale_channel_data}\\nTotal workspace channels: {total_channels}",
        [
          {
            field: "classifications",
            as: "channel_classifications",
            type: "string",
          },
          { field: "archive_ids", as: "archive_ids", type: "string" },
          { field: "archive_count", as: "archive_count", type: "number" },
          { field: "review_count", as: "review_count", type: "number" },
        ],
      ),

      // 5. Condition: any channels to archive?
      n("5", "condition", "Any channels flagged for archival?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "archive_count", operator: "greater_than", value: "0" },
          ],
        },
      }),

      // 6. Auto-archive channels via Slack API
      n("6", "hitApi", "Slack API: Archive stale channels", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/conversations.archive",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "channel", value: "{archive_ids}" }],
          variables: [
            { name: "ok", customName: "archive_success", type: "boolean" },
          ],
        },
      }),

      // 7. Audit log to Google Sheets
      n("7", "hitApi", "Log archival audit to Google Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ChannelAudit!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{total_channels}","{archive_count}","{review_count}","{channel_classifications}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),

      // 8. Admin summary to Slack
      slackNode(
        "8",
        "Post admin audit summary to Slack",
        1720,
        "Channel Audit Complete\\n\\nTotal channels scanned: {total_channels}\\nArchived: {archive_count}\\nFlagged for review: {review_count}\\nClassification details: {channel_classifications}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 249. SLACK CHANNEL SUMMARIZER (7 nodes)
  // ===================================================================

  "Slack Channel Summarizer": {
    summary:
      "On a daily digest schedule, pulls messages from monitored Slack channels, strips bot noise, uses AI to generate structured summaries with decisions/blockers/action items, filters low-activity channels, posts digests to Slack, and archives summaries for historical reference.",
    nodes: [
      trigger("Daily channel digest schedule", "schedule", {
        schedule: "Every weekday at 6:00 PM UTC",
        howItStarts:
          "Runs daily at end of business to generate channel summaries for the team",
        setup:
          "Configure monitored channel IDs in the Slack API node. Ensure the bot has channels:history scope for each target channel.",
        inputFields: [],
      }),

      // 2. Pull messages from monitored channels
      n("2", "hitApi", "Slack API: Fetch channel messages (last 24h)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://slack.com/api/conversations.history?channel={monitored_channel_id}&oldest={yesterday_ts}&limit=200",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
          ],
          body: [],
          variables: [
            { name: "messages", customName: "raw_messages", type: "string" },
            {
              name: "has_more",
              customName: "has_more_messages",
              type: "boolean",
            },
          ],
        },
      }),

      // 3. Remove bot noise
      n("3", "runCode", "Filter bot messages and noise", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Remove bot messages, join/leave events, and automated notifications
const messages = JSON.parse(inputs.raw_messages || '[]');
const humanMessages = messages.filter(m => {
  if (m.subtype === 'bot_message' || m.bot_id) return false;
  if (m.subtype === 'channel_join' || m.subtype === 'channel_leave') return false;
  if (m.subtype === 'channel_purpose' || m.subtype === 'channel_topic') return false;
  return true;
});
const messageCount = humanMessages.length;
const uniqueAuthors = [...new Set(humanMessages.map(m => m.user))].length;
const threadCount = humanMessages.filter(m => m.thread_ts && m.thread_ts === m.ts).length;
const cleanedText = humanMessages.map(m => m.text).join('\\n---\\n');
const lowActivity = messageCount < 5 ? 'true' : 'false';
return {
  cleaned_messages: cleanedText,
  message_count: messageCount,
  unique_authors: uniqueAuthors,
  thread_count: threadCount,
  low_activity: lowActivity,
};`,
          inputVariables: [{ name: "raw_messages", type: "string" }],
          outputVariables: [
            { name: "cleaned_messages", type: "string" },
            { name: "message_count", type: "number" },
            { name: "unique_authors", type: "number" },
            { name: "thread_count", type: "number" },
            { name: "low_activity", type: "string" },
          ],
        },
      }),

      // 4. AI summary with structured output
      aiNode(
        "4",
        "AI summarize: decisions, blockers, actions",
        760,
        "You are a team communication analyst. Summarize the day's Slack conversation into structured sections: key_decisions (what was decided), blockers (issues raised without resolution), action_items (tasks assigned or volunteered for with owners), highlights (notable achievements or announcements). Be concise but complete. Return JSON: {key_decisions: [string], blockers: [string], action_items: [{task, owner}], highlights: [string], summary_text: string, urgency_level: string}",
        "Channel messages from today ({message_count} messages, {unique_authors} participants, {thread_count} threads):\\n{cleaned_messages}",
        [
          { field: "key_decisions", as: "key_decisions", type: "string" },
          { field: "blockers", as: "blockers", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "summary_text", as: "summary_text", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      // 5. Condition: skip low activity channels
      n("5", "condition", "Skip if low activity channel", 1000, {
        conditionDetails: {
          conditions: [
            { field: "low_activity", operator: "equals", value: "false" },
          ],
        },
      }),

      // 6. Post digest to Slack
      slackNode(
        "6",
        "Post daily digest to Slack",
        1240,
        "Daily Channel Digest ({message_count} messages, {unique_authors} people)\\n\\nDecisions: {key_decisions}\\n\\nBlockers: {blockers}\\n\\nAction Items: {action_items}\\n\\nHighlights: {summary_text}",
      ),

      // 7. Archive summary log
      n("7", "hitApi", "Archive summary to Google Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/DailyDigests!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{monitored_channel_id}","{message_count}","{unique_authors}","{key_decisions}","{blockers}","{action_items}","{summary_text}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 250. SLACK EMOJI REACTION WORKFLOW (8 nodes)
  // ===================================================================

  "Slack Emoji Reaction Workflow": {
    summary:
      "Listens for emoji reactions on Slack messages, maps specific emojis to actions (bug report, bookmark, review request, approval), fetches the source message context, routes to the appropriate service (Jira for bugs, Notion for bookmarks, Slack for reviews), and logs errors for unmapped reactions.",
    nodes: [
      trigger("Slack reaction_added webhook", "webhook_app", {
        source: "Slack Events API",
        event: "reaction_added",
        howItStarts:
          "Fires whenever a user adds an emoji reaction to a Slack message",
        setup:
          "In Slack app settings -> Event Subscriptions, subscribe to reaction_added events. Set the webhook URL to this workflow endpoint. Required scopes: reactions:read, channels:history, chat:write.",
        inputFields: [],
      }),

      // 2. Map emoji to action
      n("2", "runCode", "Map emoji reaction to action type", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Map known emoji names to workflow actions
const payload = JSON.parse(inputs.webhook_payload || '{}');
const event = payload.event || payload;
const emoji = event.reaction || '';
const channelId = event.item?.channel || '';
const messageTs = event.item?.ts || '';
const userId = event.user || '';

const emojiMap = {
  'bug': 'create_bug',
  'beetle': 'create_bug',
  'bookmark': 'save_bookmark',
  'floppy_disk': 'save_bookmark',
  'eyes': 'request_review',
  'mag': 'request_review',
  'white_check_mark': 'approve',
  'heavy_check_mark': 'approve',
  'thumbsup': 'approve',
};

const action = emojiMap[emoji] || 'unknown';
return {
  emoji_name: emoji,
  action_type: action,
  channel_id: channelId,
  message_ts: messageTs,
  reactor_user_id: userId,
  is_known_action: action !== 'unknown' ? 'true' : 'false',
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "emoji_name", type: "string" },
            { name: "action_type", type: "string" },
            { name: "channel_id", type: "string" },
            { name: "message_ts", type: "string" },
            { name: "reactor_user_id", type: "string" },
            { name: "is_known_action", type: "string" },
          ],
        },
      }),

      // 3. Fetch source message from Slack
      n("3", "hitApi", "Slack API: Fetch reacted message", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://slack.com/api/conversations.history?channel={channel_id}&latest={message_ts}&inclusive=true&limit=1",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
          ],
          body: [],
          variables: [
            {
              name: "messages[0].text",
              customName: "source_message_text",
              type: "string",
            },
            {
              name: "messages[0].user",
              customName: "source_author_id",
              type: "string",
            },
          ],
        },
      }),

      // 4. Route condition: bug vs bookmark vs review vs approve
      n("4", "condition", "Route by action type: is bug report?", 760, {
        conditionDetails: {
          conditions: [
            { field: "action_type", operator: "equals", value: "create_bug" },
          ],
        },
      }),

      // 5. Jira bug creation (True path from condition)
      n("5", "hitApi", "Create Jira bug from reacted message", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{jira_project_key}"},"summary":"[Slack Bug] {source_message_text}","issuetype":{"name":"Bug"},"description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Reported via Slack reaction by <@{reactor_user_id}> in channel {channel_id}:\\n\\n{source_message_text}"}]}]},"labels":["slack-reaction","auto-created"]}',
            },
          ],
          variables: [
            { name: "key", customName: "jira_issue_key", type: "string" },
            { name: "id", customName: "jira_issue_id", type: "string" },
          ],
        },
      }),

      // 6. Bookmark save (False path: non-bug actions proceed here; handles bookmark/review/approve)
      n(
        "6",
        "hitApi",
        "Save bookmark to Notion database",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.notion.com/v1/pages",
            headers: [
              { key: "Authorization", value: "Bearer {notion_api_key}" },
              { key: "Notion-Version", value: "2022-06-28" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "parent",
                value: '{"database_id":"{notion_bookmarks_db}"}',
              },
              {
                key: "properties",
                value:
                  '{"Title":{"title":[{"text":{"content":"{source_message_text}"}}]},"Source":{"rich_text":[{"text":{"content":"Slack #{channel_id}"}}]},"Saved By":{"rich_text":[{"text":{"content":"<@{reactor_user_id}>"}}]},"Action":{"select":{"name":"{action_type}"}},"Date":{"date":{"start":"{date}"}}}',
              },
            ],
            variables: [
              { name: "id", customName: "notion_page_id", type: "string" },
            ],
          },
        },
        460,
      ),

      // 7. Notify reviewer via Slack thread reply
      slackNode(
        "7",
        "Post review/confirmation thread reply",
        1240,
        "Reaction processed: :{emoji_name}: by <@{reactor_user_id}>\\nAction: {action_type}\\nResult: Bug -> {jira_issue_key} | Bookmark/Review -> saved to Notion",
      ),

      // 8. Error annotation for unmapped emoji
      n("8", "hitApi", "Log unmapped reaction to error sheet", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ReactionErrors!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{emoji_name}","{action_type}","{reactor_user_id}","{channel_id}","{source_message_text}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "error_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 251. SLACK FILE UPLOAD TO GOOGLE DRIVE (8 nodes)
  // ===================================================================

  "Slack File Upload to Google Drive": {
    summary:
      "Detects files shared in Slack, classifies the file type, validates security and size constraints, downloads from Slack, uploads to a structured Google Drive folder, verifies integrity, logs the transfer to Sheets, and confirms back in the Slack thread.",
    nodes: [
      trigger("Slack file_shared webhook", "webhook_app", {
        source: "Slack Events API",
        event: "file_shared",
        howItStarts:
          "Fires whenever a file is shared in a monitored Slack channel",
        setup:
          "In Slack app settings -> Event Subscriptions, subscribe to file_shared events. Ensure bot has files:read scope. Set Google Drive folder IDs by file type in the runCode node.",
        inputFields: [],
      }),

      // 2. Classify file type and extract metadata
      n("2", "runCode", "Classify file and extract metadata", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Parse file shared event and classify
const payload = JSON.parse(inputs.webhook_payload || '{}');
const file = payload.event?.file || payload.file || {};
const fileName = file.name || 'unknown';
const fileSize = file.size || 0;
const mimeType = file.mimetype || '';
const fileId = file.id || '';
const channelId = payload.event?.channel_id || '';
const userId = payload.event?.user_id || file.user || '';

// Classify into drive folders
let category = 'general';
if (mimeType.includes('image')) category = 'images';
else if (mimeType.includes('pdf') || mimeType.includes('document')) category = 'documents';
else if (mimeType.includes('spreadsheet') || mimeType.includes('csv')) category = 'spreadsheets';
else if (mimeType.includes('presentation')) category = 'presentations';
else if (mimeType.includes('video')) category = 'videos';
else if (mimeType.includes('zip') || mimeType.includes('archive')) category = 'archives';

const sizeMB = Math.round(fileSize / (1024 * 1024) * 100) / 100;
const isSecure = !fileName.match(/\\.(exe|bat|cmd|sh|ps1)$/i) ? 'true' : 'false';
const withinSizeLimit = sizeMB <= 100 ? 'true' : 'false';
const passesValidation = isSecure === 'true' && withinSizeLimit === 'true' ? 'true' : 'false';

return {
  file_id: fileId,
  file_name: fileName,
  file_size_mb: sizeMB,
  mime_type: mimeType,
  file_category: category,
  channel_id: channelId,
  uploader_user_id: userId,
  is_secure: isSecure,
  within_size_limit: withinSizeLimit,
  passes_validation: passesValidation,
  download_url: file.url_private || '',
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "file_id", type: "string" },
            { name: "file_name", type: "string" },
            { name: "file_size_mb", type: "number" },
            { name: "mime_type", type: "string" },
            { name: "file_category", type: "string" },
            { name: "channel_id", type: "string" },
            { name: "uploader_user_id", type: "string" },
            { name: "passes_validation", type: "string" },
            { name: "download_url", type: "string" },
          ],
        },
      }),

      // 3. Security/size validation condition
      n("3", "condition", "File passes security and size checks?", 520, {
        conditionDetails: {
          conditions: [
            { field: "passes_validation", operator: "equals", value: "true" },
          ],
        },
      }),

      // 4. Download file from Slack
      n("4", "hitApi", "Slack API: Download file content", 760, {
        hitApiDetails: {
          method: "GET",
          url: "{download_url}",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
          ],
          body: [],
          variables: [
            { name: "content", customName: "file_content", type: "string" },
          ],
        },
      }),

      // 5. Upload to Google Drive
      n("5", "hitApi", "Upload to Google Drive folder", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{file_name}" },
            { key: "parents", value: '["{google_drive_folder_id}"]' },
            { key: "mimeType", value: "{mime_type}" },
            {
              key: "description",
              value:
                "Uploaded from Slack #{channel_id} by <@{uploader_user_id}> | Category: {file_category}",
            },
          ],
          variables: [
            { name: "id", customName: "drive_file_id", type: "string" },
            {
              name: "webViewLink",
              customName: "drive_view_link",
              type: "string",
            },
          ],
        },
      }),

      // 6. Integrity verification
      n("6", "runCode", "Verify upload integrity", 1240, {
        codeDetails: {
          language: "javascript",
          code: `// Verify the upload completed successfully
const driveFileId = inputs.drive_file_id || '';
const driveViewLink = inputs.drive_view_link || '';
const originalName = inputs.file_name || '';
const originalSizeMB = inputs.file_size_mb || 0;

const uploadSuccess = driveFileId.length > 0 ? 'true' : 'false';
const verificationStatus = uploadSuccess === 'true' ? 'verified' : 'failed';
const transferSummary = uploadSuccess === 'true'
  ? originalName + ' (' + originalSizeMB + ' MB) -> Drive ID: ' + driveFileId
  : 'FAILED: ' + originalName + ' did not upload successfully';

return {
  upload_success: uploadSuccess,
  verification_status: verificationStatus,
  transfer_summary: transferSummary,
};`,
          inputVariables: [
            { name: "drive_file_id", type: "string" },
            { name: "drive_view_link", type: "string" },
            { name: "file_name", type: "string" },
            { name: "file_size_mb", type: "number" },
          ],
          outputVariables: [
            { name: "upload_success", type: "string" },
            { name: "verification_status", type: "string" },
            { name: "transfer_summary", type: "string" },
          ],
        },
      }),

      // 7. Audit logging
      n("7", "hitApi", "Log file transfer to Google Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/FileTransfers!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{file_name}","{file_size_mb}","{file_category}","{uploader_user_id}","{channel_id}","{drive_file_id}","{verification_status}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),

      // 8. Thread confirmation in Slack
      slackNode(
        "8",
        "Confirm upload in Slack thread",
        1720,
        "File backed up to Google Drive\\n\\nFile: {file_name} ({file_size_mb} MB)\\nCategory: {file_category}\\nStatus: {verification_status}\\nDrive link: {drive_view_link}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 252. SLACK MESSAGE TO TO-DO CREATOR (7 nodes)
  // ===================================================================

  "Slack Message to To-Do Creator": {
    summary:
      "Captures Slack messages flagged via emoji or shortcut, fetches the full message and thread context, uses AI to extract task title/assignee/due date, validates fields, creates a task in Asana or Todoist, confirms in the Slack thread, and checks for duplicate tasks.",
    nodes: [
      trigger("Slack task emoji or shortcut webhook", "webhook_app", {
        source: "Slack Events API / Shortcuts",
        event: "reaction_added / shortcut",
        howItStarts:
          "A user reacts with a task emoji (e.g. :todo:, :white_check_mark:) or uses a Slack shortcut to convert a message into a to-do",
        setup:
          "Subscribe to reaction_added events and configure a message shortcut in Slack App settings. Map the :todo: emoji or shortcut callback_id to this workflow URL.",
        inputFields: [],
      }),

      // 2. Fetch full message + thread context
      n("2", "hitApi", "Slack API: Fetch message and thread", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://slack.com/api/conversations.replies?channel={channel_id}&ts={message_ts}&limit=20",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
          ],
          body: [],
          variables: [
            { name: "messages", customName: "thread_messages", type: "string" },
            {
              name: "messages[0].text",
              customName: "root_message_text",
              type: "string",
            },
            {
              name: "messages[0].user",
              customName: "message_author",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI task extraction
      aiNode(
        "3",
        "AI extract task: title, assignee, due date",
        520,
        "You are a task extraction assistant. From the Slack message and thread context, extract: task_title (concise actionable title), assignee (mentioned user or 'unassigned'), due_date (if mentioned, ISO format, else 'none'), priority (high/medium/low based on urgency words), project_context (which project or topic this relates to), original_quote (the exact text that triggered the task). Return JSON: {task_title: string, assignee: string, due_date: string, priority: string, project_context: string, original_quote: string}",
        "Root message: {root_message_text}\\nFull thread: {thread_messages}\\nRequested by: <@{message_author}>",
        [
          { field: "task_title", as: "task_title", type: "string" },
          { field: "assignee", as: "task_assignee", type: "string" },
          { field: "due_date", as: "task_due_date", type: "string" },
          { field: "priority", as: "task_priority", type: "string" },
          { field: "project_context", as: "project_context", type: "string" },
          { field: "original_quote", as: "original_quote", type: "string" },
        ],
      ),

      // 4. Field validation
      n("4", "runCode", "Validate extracted task fields", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Validate and normalize task fields
const title = inputs.task_title || '';
const assignee = inputs.task_assignee || 'unassigned';
const dueDate = inputs.task_due_date || 'none';
const priority = inputs.task_priority || 'medium';

const titleValid = title.length >= 5 && title.length <= 200;
const hasDueDate = dueDate !== 'none' && dueDate.match(/^\\d{4}-\\d{2}-\\d{2}/);

// Map priority to Asana-compatible values
const priorityMap = { high: 'high', medium: 'medium', low: 'low' };
const normalizedPriority = priorityMap[priority.toLowerCase()] || 'medium';

// Generate dedup key from title + assignee
const dedupKey = (title.toLowerCase().replace(/[^a-z0-9]/g, '') + '_' + assignee).substring(0, 100);

return {
  validated_title: titleValid ? title : 'Task from Slack: ' + title.substring(0, 150),
  validated_assignee: assignee,
  validated_due_date: hasDueDate ? dueDate : '',
  normalized_priority: normalizedPriority,
  dedup_key: dedupKey,
  fields_valid: titleValid ? 'true' : 'false',
};`,
          inputVariables: [
            { name: "task_title", type: "string" },
            { name: "task_assignee", type: "string" },
            { name: "task_due_date", type: "string" },
            { name: "task_priority", type: "string" },
          ],
          outputVariables: [
            { name: "validated_title", type: "string" },
            { name: "validated_assignee", type: "string" },
            { name: "validated_due_date", type: "string" },
            { name: "normalized_priority", type: "string" },
            { name: "dedup_key", type: "string" },
            { name: "fields_valid", type: "string" },
          ],
        },
      }),

      // 5. Create task in Asana
      n("5", "hitApi", "Create task in Asana", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"{validated_title}","notes":"From Slack: {original_quote}\\n\\nProject: {project_context}\\nPriority: {normalized_priority}\\nCreated by: <@{message_author}>","projects":["{asana_project_id}"],"due_on":"{validated_due_date}","custom_fields":{"{priority_field_gid}":"{normalized_priority}"}}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Confirm in Slack thread
      slackNode(
        "6",
        "Confirm task creation in Slack thread",
        1240,
        "Task created from this message\\n\\nTitle: {validated_title}\\nAssignee: {validated_assignee}\\nDue: {validated_due_date}\\nPriority: {normalized_priority}\\nAsana: {asana_task_url}",
      ),

      // 7. Duplicate check condition
      n("7", "condition", "Check if duplicate task exists", 1480, {
        conditionDetails: {
          conditions: [
            { field: "fields_valid", operator: "equals", value: "true" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "7"),
      e("7", "5", "output_1"),
      e("7", "6", "output_2"),
      e("5", "6"),
    ],
  },

  // ===================================================================
  // 253. SLACK STANDUP COLLECTION BOT (9 nodes)
  // ===================================================================

  "Slack Standup Collection Bot": {
    summary:
      "Runs daily to filter the team roster for PTO, sends standup prompts via DM, waits for the collection window, gathers responses, uses AI to detect blockers and generate a team summary, posts the summary to a team channel, reminds non-responders, and archives everything to Sheets.",
    nodes: [
      trigger("Daily standup collection schedule", "schedule", {
        schedule: "Every weekday at 9:00 AM in team timezone",
        howItStarts:
          "Triggers daily to collect standup updates from the team via Slack DMs",
        setup:
          "Configure team member Slack IDs in the roster runCode node. Set the standup channel ID for summary posting. Optionally integrate with PTO calendar API.",
        inputFields: [],
      }),

      // 2. Team roster + PTO filter
      n("2", "runCode", "Build team roster and filter PTO", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Team roster with PTO filtering
const teamRoster = [
  { id: 'U001', name: 'Alice', role: 'engineer' },
  { id: 'U002', name: 'Bob', role: 'engineer' },
  { id: 'U003', name: 'Carol', role: 'designer' },
  { id: 'U004', name: 'Dave', role: 'pm' },
  { id: 'U005', name: 'Eve', role: 'engineer' },
];
// In production, fetch PTO from Google Calendar or BambooHR
const ptoUsers = []; // IDs on PTO today
const activeMembers = teamRoster.filter(m => !ptoUsers.includes(m.id));
const ptoCount = teamRoster.length - activeMembers.length;
const memberIds = activeMembers.map(m => m.id).join(',');
const memberNames = activeMembers.map(m => m.name).join(', ');
const today = new Date().toISOString().split('T')[0];

return {
  active_member_ids: memberIds,
  active_member_names: memberNames,
  active_count: activeMembers.length,
  pto_count: ptoCount,
  standup_date: today,
  roster_json: JSON.stringify(activeMembers),
};`,
          inputVariables: [],
          outputVariables: [
            { name: "active_member_ids", type: "string" },
            { name: "active_member_names", type: "string" },
            { name: "active_count", type: "number" },
            { name: "pto_count", type: "number" },
            { name: "standup_date", type: "string" },
            { name: "roster_json", type: "string" },
          ],
        },
      }),

      // 3. Send DM standup prompts
      n("3", "hitApi", "Slack API: Send standup DM prompts", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/chat.postMessage",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "channel", value: "{active_member_ids}" },
            {
              key: "text",
              value:
                "Good morning! Time for standup ({standup_date})\\n\\n1. What did you accomplish yesterday?\\n2. What are you working on today?\\n3. Any blockers or help needed?\\n\\nReply here within 30 minutes.",
            },
          ],
          variables: [
            { name: "ok", customName: "prompts_sent", type: "boolean" },
            { name: "ts", customName: "prompt_timestamp", type: "string" },
          ],
        },
      }),

      // 4. Collection window delay
      n("4", "delayAction", "Wait 30 min for standup responses", 760, {
        delayAction: { time: 30, timeFormat: "minutes" },
      }),

      // 5. Collect responses
      n("5", "hitApi", "Slack API: Collect DM responses", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://slack.com/api/conversations.history?channel={dm_channel_id}&oldest={prompt_timestamp}&limit=50",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
          ],
          body: [],
          variables: [
            {
              name: "messages",
              customName: "standup_responses",
              type: "string",
            },
          ],
        },
      }),

      // 6. AI blocker detection + summary
      aiNode(
        "6",
        "AI detect blockers and summarize standups",
        1240,
        "You are a standup summarizer. Analyze each team member response and extract: yesterday's accomplishments, today's plans, and blockers. Flag any blockers as critical/minor. Generate a team-wide summary highlighting cross-team dependencies and common themes. Return JSON: {team_summary: string, blocker_count: number, critical_blockers: [string], member_summaries: [{name, yesterday, today, blockers}], response_count: number, non_responders: [string], themes: [string]}",
        "Team members: {active_member_names}\\nStandup date: {standup_date}\\nResponses collected:\\n{standup_responses}",
        [
          { field: "team_summary", as: "team_summary", type: "string" },
          { field: "blocker_count", as: "blocker_count", type: "number" },
          {
            field: "critical_blockers",
            as: "critical_blockers",
            type: "string",
          },
          { field: "member_summaries", as: "member_summaries", type: "string" },
          { field: "response_count", as: "response_count", type: "number" },
          { field: "non_responders", as: "non_responders", type: "string" },
        ],
      ),

      // 7. Post team summary
      slackNode(
        "7",
        "Post standup summary to team channel",
        1480,
        "Standup Summary - {standup_date}\\n\\n{team_summary}\\n\\nBlockers ({blocker_count}): {critical_blockers}\\n\\nResponses: {response_count}/{active_count} | PTO: {pto_count}",
      ),

      // 8. Non-responder reminders
      n("8", "hitApi", "Slack API: Remind non-responders", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/chat.postMessage",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "channel", value: "{non_responders}" },
            {
              key: "text",
              value:
                "Friendly reminder: your standup for {standup_date} is missing. Please reply to the earlier DM or post directly in the standup channel.",
            },
          ],
          variables: [
            { name: "ok", customName: "reminders_sent", type: "boolean" },
          ],
        },
      }),

      // 9. Archive to Sheets
      n("9", "hitApi", "Archive standup data to Google Sheets", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Standups!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{standup_date}","{active_count}","{response_count}","{blocker_count}","{team_summary}","{critical_blockers}","{non_responders}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 254. SLACK WORKFLOW APPROVAL BOT (8 nodes)
  // ===================================================================

  "Slack Workflow Approval Bot": {
    summary:
      "Accepts approval requests via slash command, uses AI to parse the request details, resolves the approval chain based on type and amount, posts interactive approval cards to Slack, captures the decision, routes approved requests to execution or rejection notifications, and maintains a full audit trail in Sheets.",
    nodes: [
      trigger("Slash command: /approve webhook", "webhook_app", {
        source: "Slack Slash Command",
        event: "slash_command.approve",
        howItStarts:
          "A user submits an approval request via the /approve slash command in Slack",
        setup:
          "Create a slash command /approve in Slack App settings pointing to this workflow URL. The command payload includes the request text entered by the user.",
        inputFields: [],
      }),

      // 2. AI parse the approval request
      aiNode(
        "2",
        "AI parse approval request details",
        280,
        "You are an approval request parser. Extract the request type (expense, access, hiring, time-off, purchase, deployment), amount if applicable, urgency (standard/urgent/critical), justification, and requestor details from the slash command payload. Return JSON: {request_type: string, amount: number, currency: string, urgency: string, justification: string, requestor_name: string, requestor_id: string, department: string, description: string}",
        "{webhook_payload}",
        [
          { field: "request_type", as: "request_type", type: "string" },
          { field: "amount", as: "request_amount", type: "number" },
          { field: "urgency", as: "request_urgency", type: "string" },
          {
            field: "justification",
            as: "request_justification",
            type: "string",
          },
          { field: "requestor_name", as: "requestor_name", type: "string" },
          { field: "requestor_id", as: "requestor_id", type: "string" },
          { field: "description", as: "request_description", type: "string" },
        ],
      ),

      // 3. Resolve approval chain
      n("3", "runCode", "Resolve approval chain by type and amount", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Determine approvers based on request type and amount thresholds
const type = inputs.request_type || 'general';
const amount = parseFloat(inputs.request_amount) || 0;
const urgency = inputs.request_urgency || 'standard';

const approvalChains = {
  expense: amount > 5000 ? ['manager', 'finance_director', 'cfo'] : amount > 1000 ? ['manager', 'finance_director'] : ['manager'],
  access: ['security_lead', 'it_admin'],
  hiring: ['hiring_manager', 'hr_director', 'vp'],
  'time-off': ['direct_manager'],
  purchase: amount > 10000 ? ['procurement', 'finance_director', 'cfo'] : ['procurement', 'finance_director'],
  deployment: ['tech_lead', 'engineering_manager'],
};

const chain = approvalChains[type] || ['manager'];
const approverCount = chain.length;
const currentApprover = chain[0];
const slaHours = urgency === 'critical' ? 2 : urgency === 'urgent' ? 8 : 24;
const requestId = 'REQ-' + Date.now().toString(36).toUpperCase();

return {
  approval_chain: JSON.stringify(chain),
  approver_count: approverCount,
  current_approver_role: currentApprover,
  sla_hours: slaHours,
  request_id: requestId,
  chain_summary: chain.join(' -> '),
};`,
          inputVariables: [
            { name: "request_type", type: "string" },
            { name: "request_amount", type: "number" },
            { name: "request_urgency", type: "string" },
          ],
          outputVariables: [
            { name: "approval_chain", type: "string" },
            { name: "approver_count", type: "number" },
            { name: "current_approver_role", type: "string" },
            { name: "sla_hours", type: "number" },
            { name: "request_id", type: "string" },
            { name: "chain_summary", type: "string" },
          ],
        },
      }),

      // 4. Post approval card to Slack
      n("4", "hitApi", "Post interactive approval card to Slack", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/chat.postMessage",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "channel", value: "{approvals_channel_id}" },
            {
              key: "text",
              value:
                "Approval Request {request_id}\\n\\nType: {request_type} | Amount: {request_amount}\\nFrom: {requestor_name}\\nUrgency: {request_urgency} (SLA: {sla_hours}h)\\nJustification: {request_justification}\\nApproval chain: {chain_summary}\\nCurrent approver: {current_approver_role}\\n\\nPlease react with :white_check_mark: to approve or :x: to reject.",
            },
            {
              key: "blocks",
              value:
                '[{"type":"section","text":{"type":"mrkdwn","text":"*Approval Request {request_id}*\\nType: {request_type} | Amount: ${request_amount}\\nFrom: <@{requestor_id}>\\nUrgency: {request_urgency}\\nJustification: {request_justification}"}},{"type":"actions","elements":[{"type":"button","text":{"type":"plain_text","text":"Approve"},"style":"primary","action_id":"approve_{request_id}"},{"type":"button","text":{"type":"plain_text","text":"Reject"},"style":"danger","action_id":"reject_{request_id}"}]}]',
            },
          ],
          variables: [
            { name: "ok", customName: "card_posted", type: "boolean" },
            { name: "ts", customName: "approval_card_ts", type: "string" },
          ],
        },
      }),

      // 5. Approval gate for decision capture
      n("5", "approvalGate", "Capture approval or rejection decision", 1000, {
        approvalGateData: {
          message:
            "Awaiting decision on {request_id}: {request_type} for {request_amount} from {requestor_name}. Approval chain: {chain_summary}. SLA: {sla_hours} hours.",
        },
      }),

      // 6. Condition: approved or rejected?
      n("6", "condition", "Request approved?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "approval_status", operator: "equals", value: "approved" },
          ],
        },
      }),

      // 7. Execution trigger / rejection notify
      slackNode(
        "7",
        "Notify outcome to requestor",
        1480,
        "Request {request_id} Decision\\n\\nStatus: Processed\\nType: {request_type}\\nAmount: {request_amount}\\nRequestor: <@{requestor_id}>\\nApproval chain: {chain_summary}\\nDecision captured — proceeding with execution or rejection notification.",
      ),

      // 8. Audit trail in Sheets
      n("8", "hitApi", "Log approval decision to audit trail", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ApprovalAudit!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{request_id}","{request_type}","{request_amount}","{requestor_name}","{request_urgency}","{chain_summary}","{approval_status}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "audit_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 255. SOCIAL PROOF COLLECTOR (8 nodes)
  // ===================================================================

  "Social Proof Collector": {
    summary:
      "On a schedule, harvests reviews and mentions from multiple sources (G2, Trustpilot, Twitter), filters spam and low-quality entries, uses AI to score authenticity and marketing value, routes quality content through a marketing approval gate, sends consent requests to authors, and logs approved social proof to a library spreadsheet.",
    nodes: [
      trigger("Scheduled social proof harvest", "schedule", {
        schedule: "Every Monday and Thursday at 10:00 AM UTC",
        howItStarts:
          "Runs on schedule to harvest new reviews, testimonials, and social mentions from multiple platforms",
        setup:
          "Configure API keys for G2, Trustpilot, and Twitter/X. Set quality thresholds in the AI scoring node. Create a Google Sheet for the social proof library.",
        inputFields: [],
      }),

      // 2. Multi-source review/mention pull
      n("2", "hitApi", "Pull reviews from G2 and Trustpilot", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://data.g2.com/api/v1/products/{g2_product_id}/reviews?filter[created_at][gte]={last_harvest_date}",
          headers: [
            { key: "Authorization", value: "Token token={g2_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "raw_reviews", type: "string" },
            {
              name: "meta.record_count",
              customName: "review_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Spam/quality filtering
      n("3", "runCode", "Filter spam and low-quality content", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Filter out spam, very short reviews, and suspicious patterns
const reviews = JSON.parse(inputs.raw_reviews || '[]');
const filtered = reviews.filter(r => {
  const text = r.attributes?.comment_body || r.text || r.content || '';
  // Remove very short reviews
  if (text.length < 50) return false;
  // Remove reviews with suspicious patterns (all caps, excessive punctuation)
  if (text === text.toUpperCase() && text.length > 20) return false;
  if ((text.match(/!/g) || []).length > 10) return false;
  // Remove likely spam keywords
  const spamPatterns = /\\b(buy now|click here|visit my|free money|casino)\\b/i;
  if (spamPatterns.test(text)) return false;
  return true;
});

const qualityCount = filtered.length;
const spamRemoved = reviews.length - filtered.length;

return {
  filtered_reviews: JSON.stringify(filtered),
  quality_count: qualityCount,
  spam_removed: spamRemoved,
  has_quality_content: qualityCount > 0 ? 'true' : 'false',
};`,
          inputVariables: [{ name: "raw_reviews", type: "string" }],
          outputVariables: [
            { name: "filtered_reviews", type: "string" },
            { name: "quality_count", type: "number" },
            { name: "spam_removed", type: "number" },
            { name: "has_quality_content", type: "string" },
          ],
        },
      }),

      // 4. AI scoring
      aiNode(
        "4",
        "AI score authenticity and marketing value",
        760,
        "You are a social proof analyst. Score each review/mention on: authenticity (1-10, detecting fake or incentivized reviews), specificity (1-10, mentions concrete features/outcomes vs generic praise), marketing_strength (1-10, quotable and compelling for prospects), sentiment_clarity (positive/mixed/negative). Also extract the best quote snippet for marketing use. Return JSON: {scored_items: [{text, author, source, authenticity, specificity, strength, sentiment, best_quote, overall_score}], top_items: [{text, author, score, best_quote}], average_score: number, marketing_ready_count: number}",
        "Reviews to score ({quality_count} items):\\n{filtered_reviews}",
        [
          { field: "scored_items", as: "scored_items", type: "string" },
          { field: "top_items", as: "top_items", type: "string" },
          { field: "average_score", as: "average_score", type: "number" },
          {
            field: "marketing_ready_count",
            as: "marketing_ready_count",
            type: "number",
          },
        ],
      ),

      // 5. Quality condition
      n("5", "condition", "Any marketing-ready social proof?", 1000, {
        conditionDetails: {
          conditions: [
            {
              field: "marketing_ready_count",
              operator: "greater_than",
              value: "0",
            },
          ],
        },
      }),

      // 6. Marketing approval gate
      n("6", "approvalGate", "Marketing team reviews top social proof", 1240, {
        approvalGateData: {
          message:
            "Review {marketing_ready_count} new social proof items (avg score: {average_score}/10):\\n\\nTop items: {top_items}\\n\\nApprove to send consent requests to authors.",
        },
      }),

      // 7. Consent request email
      emailNode(
        "7",
        "Send consent request to review authors",
        1480,
        "{author_email}",
        "May we feature your review of {product_name}?",
        'Hi {author_name},\\n\\nThank you for your wonderful review! We would love to feature your feedback on our website and marketing materials.\\n\\nHere is the excerpt we would like to use:\\n\\"{best_quote}\\"\\n\\nWould you be comfortable with us sharing this? Simply reply YES to give consent, or let us know if you would like any changes.\\n\\nBest regards,\\nThe {company_name} Team',
      ),

      // 8. Library logging
      n("8", "hitApi", "Log approved proof to library spreadsheet", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/SocialProofLibrary!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{quality_count}","{marketing_ready_count}","{average_score}","{top_items}","{spam_removed}","consent_pending"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 256. TEAMS CHANNEL AUTO CREATOR (7 nodes)
  // ===================================================================

  "Teams Channel Auto Creator": {
    summary:
      "When a new project is created (via webhook), generates a standardized channel name and purpose, checks for naming collisions, creates the Microsoft Teams channel, adds project members, posts a welcome message with context, and logs the provisioning to Sheets.",
    nodes: [
      trigger("Project created webhook", "webhook_app", {
        source: "Jira / Asana / Internal PM Tool",
        event: "project.created",
        howItStarts:
          "A new project is created in the project management system, triggering automatic Teams channel provisioning",
        setup:
          "Configure your PM tool to POST project creation events (project name, description, members, start date) to this workflow URL. Ensure a Microsoft Graph API app registration with Team.ReadWrite.All permission.",
        inputFields: [],
      }),

      // 2. Generate channel name and purpose
      n("2", "runCode", "Generate standardized channel name and purpose", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Parse project webhook and generate Teams channel name
const payload = JSON.parse(inputs.webhook_payload || '{}');
const projectName = payload.project_name || payload.name || 'unnamed-project';
const projectDesc = payload.description || '';
const members = payload.members || payload.team || [];
const startDate = payload.start_date || new Date().toISOString().split('T')[0];
const projectId = payload.project_id || payload.id || '';

// Generate standardized channel name (lowercase, hyphens, max 50 chars)
const channelName = 'proj-' + projectName
  .toLowerCase()
  .replace(/[^a-z0-9\\s-]/g, '')
  .replace(/\\s+/g, '-')
  .substring(0, 45);

const channelPurpose = 'Project: ' + projectName + ' | Started: ' + startDate + ' | ' + projectDesc.substring(0, 200);
const memberEmails = Array.isArray(members)
  ? members.map(m => m.email || m).filter(Boolean).join(',')
  : '';
const memberCount = memberEmails.split(',').filter(Boolean).length;

return {
  channel_name: channelName,
  channel_purpose: channelPurpose,
  project_name: projectName,
  project_description: projectDesc,
  project_id: projectId,
  member_emails: memberEmails,
  member_count: memberCount,
  start_date: startDate,
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "channel_name", type: "string" },
            { name: "channel_purpose", type: "string" },
            { name: "project_name", type: "string" },
            { name: "project_description", type: "string" },
            { name: "project_id", type: "string" },
            { name: "member_emails", type: "string" },
            { name: "member_count", type: "number" },
            { name: "start_date", type: "string" },
          ],
        },
      }),

      // 3. Collision check condition
      n("3", "condition", "Channel name collision check passed?", 520, {
        conditionDetails: {
          conditions: [
            { field: "channel_name", operator: "not_equals", value: "proj-" },
          ],
        },
      }),

      // 4. Create Teams channel
      n("4", "hitApi", "Microsoft Graph: Create Teams channel", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.microsoft.com/v1.0/teams/{team_id}/channels",
          headers: [
            { key: "Authorization", value: "Bearer {ms_graph_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "displayName", value: "{channel_name}" },
            { key: "description", value: "{channel_purpose}" },
            { key: "membershipType", value: "standard" },
          ],
          variables: [
            { name: "id", customName: "teams_channel_id", type: "string" },
            { name: "webUrl", customName: "teams_channel_url", type: "string" },
          ],
        },
      }),

      // 5. Add members
      n("5", "hitApi", "Microsoft Graph: Add project members", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.microsoft.com/v1.0/teams/{team_id}/channels/{teams_channel_id}/members",
          headers: [
            { key: "Authorization", value: "Bearer {ms_graph_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "@odata.type",
              value: "#microsoft.graph.aadUserConversationMember",
            },
            { key: "roles", value: '["owner"]' },
            {
              key: "user@odata.bind",
              value: "https://graph.microsoft.com/v1.0/users/{member_emails}",
            },
          ],
          variables: [
            { name: "id", customName: "membership_id", type: "string" },
          ],
        },
      }),

      // 6. Post welcome message
      n("6", "hitApi", "Post welcome message to new channel", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.microsoft.com/v1.0/teams/{team_id}/channels/{teams_channel_id}/messages",
          headers: [
            { key: "Authorization", value: "Bearer {ms_graph_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "body",
              value:
                '{"content":"Welcome to {project_name}!\\n\\nThis channel was auto-created for project collaboration.\\n\\nProject: {project_name}\\nDescription: {project_description}\\nStart date: {start_date}\\nTeam members: {member_count}\\n\\nChannel link: {teams_channel_url}\\n\\nPlease use this channel for all project-related discussions.","contentType":"text"}',
            },
          ],
          variables: [
            { name: "id", customName: "welcome_message_id", type: "string" },
          ],
        },
      }),

      // 7. Provisioning logging
      n("7", "hitApi", "Log channel provisioning to Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ChannelProvisioning!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{project_id}","{project_name}","{channel_name}","{teams_channel_id}","{member_count}","{teams_channel_url}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "7", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 257. TELEGRAM BOT STARTER (7 nodes)
  // ===================================================================

  "Telegram Bot Starter": {
    summary:
      "Receives Telegram webhook messages, routes by intent (start, help, chat), loads session memory from Redis for conversational context, generates AI-powered contextual replies, sends responses via Telegram API, logs interactions to Sheets, and handles errors with fallback messages.",
    nodes: [
      trigger("Telegram Bot webhook", "webhook_app", {
        source: "Telegram Bot API",
        event: "message",
        howItStarts: "A user sends a message to the Telegram bot",
        setup:
          "Create a Telegram bot via @BotFather, get the bot token. Set the webhook URL using: POST https://api.telegram.org/bot{token}/setWebhook?url={workflow_url}. Configure Redis for session storage.",
        inputFields: [],
      }),

      // 2. Intent routing
      n("2", "runCode", "Route by intent: start, help, or chat", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Parse Telegram webhook and determine intent
const payload = JSON.parse(inputs.webhook_payload || '{}');
const message = payload.message || {};
const chatId = message.chat?.id || '';
const userId = message.from?.id || '';
const userName = message.from?.first_name || 'User';
const text = (message.text || '').trim();

let intent = 'chat';
if (text === '/start') intent = 'start';
else if (text === '/help') intent = 'help';
else if (text.startsWith('/')) intent = 'command';

const sessionKey = 'telegram_session:' + chatId;

return {
  chat_id: String(chatId),
  user_id: String(userId),
  user_name: userName,
  message_text: text,
  intent: intent,
  session_key: sessionKey,
  is_chat: intent === 'chat' ? 'true' : 'false',
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "chat_id", type: "string" },
            { name: "user_id", type: "string" },
            { name: "user_name", type: "string" },
            { name: "message_text", type: "string" },
            { name: "intent", type: "string" },
            { name: "session_key", type: "string" },
            { name: "is_chat", type: "string" },
          ],
        },
      }),

      // 3. Session memory load from Redis
      n("3", "hitApi", "Load session context from Redis", 520, {
        hitApiDetails: {
          method: "GET",
          url: "{redis_rest_url}/get/{session_key}",
          headers: [{ key: "Authorization", value: "Bearer {redis_token}" }],
          body: [],
          variables: [
            { name: "result", customName: "session_history", type: "string" },
          ],
        },
      }),

      // 4. AI contextual reply
      aiNode(
        "4",
        "AI generate contextual reply",
        760,
        "You are a helpful Telegram bot assistant. Use the conversation history for context. For /start, welcome the user warmly and explain capabilities. For /help, list available commands and features. For chat messages, provide helpful, concise responses. Keep replies under 300 characters for Telegram readability. Return JSON: {reply_text: string, should_save_session: boolean, updated_context: string, response_type: string}",
        "User: {user_name}\\nIntent: {intent}\\nMessage: {message_text}\\nConversation history: {session_history}",
        [
          { field: "reply_text", as: "reply_text", type: "string" },
          {
            field: "should_save_session",
            as: "should_save_session",
            type: "string",
          },
          { field: "updated_context", as: "updated_context", type: "string" },
          { field: "response_type", as: "response_type", type: "string" },
        ],
      ),

      // 5. Send Telegram message
      n("5", "hitApi", "Telegram API: Send reply message", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.telegram.org/bot{telegram_bot_token}/sendMessage",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "chat_id", value: "{chat_id}" },
            { key: "text", value: "{reply_text}" },
            { key: "parse_mode", value: "Markdown" },
          ],
          variables: [
            { name: "ok", customName: "telegram_sent", type: "boolean" },
            {
              name: "result.message_id",
              customName: "sent_message_id",
              type: "string",
            },
          ],
        },
      }),

      // 6. Interaction logging
      n("6", "hitApi", "Log interaction to Google Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/TelegramLogs!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{chat_id}","{user_name}","{intent}","{message_text}","{reply_text}","{response_type}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),

      // 7. Error fallback handling
      n("7", "hitApi", "Telegram API: Send error fallback", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.telegram.org/bot{telegram_bot_token}/sendMessage",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "chat_id", value: "{chat_id}" },
            {
              key: "text",
              value:
                "Sorry, I encountered an issue processing your message. Please try again or type /help for available commands.",
            },
          ],
          variables: [
            { name: "ok", customName: "fallback_sent", type: "boolean" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 258. TELEGRAM NEWS ALERT BOT (8 nodes)
  // ===================================================================

  "Telegram News Alert Bot": {
    summary:
      "On a frequent schedule, pulls news feeds via SerpAPI, uses AI to score relevance and novelty, routes by urgency (breaking vs digest), deduplicates against previously sent alerts, sends immediate Telegram alerts for breaking news, batches non-urgent items into scheduled digests, and logs alert performance.",
    nodes: [
      trigger("Frequent news check schedule", "schedule", {
        schedule: "Every 15 minutes during business hours",
        howItStarts:
          "Runs frequently to check for breaking news and relevant industry updates for Telegram subscribers",
        setup:
          "Configure SerpAPI key for news search. Set target keywords and industry topics in the AI scoring node. Create a Telegram channel or group for alerts. Store previously sent article URLs in the dedup sheet.",
        inputFields: [],
      }),

      // 2. News feed pull via SerpAPI
      n("2", "hitApi", "SerpAPI: Fetch latest news articles", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?engine=google_news&q={news_keywords}&api_key={serpapi_key}&num=20",
          headers: [],
          body: [],
          variables: [
            {
              name: "news_results",
              customName: "raw_news_articles",
              type: "string",
            },
            {
              name: "search_metadata.total_time_taken",
              customName: "search_time",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI relevance + novelty scoring
      aiNode(
        "3",
        "AI score relevance, novelty, and urgency",
        520,
        "You are a news analyst. Score each article for: relevance (1-10, how related to our industry/topics), novelty (1-10, is this genuinely new information), urgency (breaking/high/medium/low). Detect duplicates of known stories. Return JSON: {scored_articles: [{title, url, source, relevance, novelty, urgency, summary, is_duplicate}], breaking_count: number, digest_count: number, total_relevant: number, breaking_articles: [{title, url, summary}], digest_articles: [{title, url, summary}]}",
        "News articles to analyze:\\n{raw_news_articles}\\nPreviously sent: {sent_article_urls}",
        [
          { field: "scored_articles", as: "scored_articles", type: "string" },
          { field: "breaking_count", as: "breaking_count", type: "number" },
          { field: "digest_count", as: "digest_count", type: "number" },
          { field: "total_relevant", as: "total_relevant", type: "number" },
          {
            field: "breaking_articles",
            as: "breaking_articles",
            type: "string",
          },
          { field: "digest_articles", as: "digest_articles", type: "string" },
        ],
      ),

      // 4. Urgency condition: breaking vs digest
      n("4", "condition", "Any breaking news requiring immediate alert?", 760, {
        conditionDetails: {
          conditions: [
            { field: "breaking_count", operator: "greater_than", value: "0" },
          ],
        },
      }),

      // 5. Dedup check
      n("5", "runCode", "Deduplicate against sent history", 1000, {
        codeDetails: {
          language: "javascript",
          code: `// Check breaking articles against recently sent URLs to prevent duplicates
const breaking = JSON.parse(inputs.breaking_articles || '[]');
const sentUrls = (inputs.sent_article_urls || '').split(',').map(u => u.trim());

const newBreaking = breaking.filter(a => !sentUrls.includes(a.url));
const dedupedCount = newBreaking.length;
const duplicatesRemoved = breaking.length - dedupedCount;

const alertText = newBreaking.map(a =>
  '🔴 BREAKING: ' + a.title + '\\n' + a.summary + '\\nRead: ' + a.url
).join('\\n\\n');

const newUrls = newBreaking.map(a => a.url).join(',');

return {
  deduped_breaking: JSON.stringify(newBreaking),
  deduped_count: dedupedCount,
  duplicates_removed: duplicatesRemoved,
  alert_text: alertText || 'No new breaking news.',
  new_urls: newUrls,
  has_new_alerts: dedupedCount > 0 ? 'true' : 'false',
};`,
          inputVariables: [
            { name: "breaking_articles", type: "string" },
            { name: "sent_article_urls", type: "string" },
          ],
          outputVariables: [
            { name: "deduped_breaking", type: "string" },
            { name: "deduped_count", type: "number" },
            { name: "duplicates_removed", type: "number" },
            { name: "alert_text", type: "string" },
            { name: "new_urls", type: "string" },
            { name: "has_new_alerts", type: "string" },
          ],
        },
      }),

      // 6. Immediate Telegram alert
      n("6", "hitApi", "Telegram API: Send breaking news alert", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.telegram.org/bot{telegram_bot_token}/sendMessage",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "chat_id", value: "{telegram_channel_id}" },
            { key: "text", value: "{alert_text}" },
            { key: "parse_mode", value: "Markdown" },
            { key: "disable_web_page_preview", value: "false" },
          ],
          variables: [
            { name: "ok", customName: "alert_sent", type: "boolean" },
            {
              name: "result.message_id",
              customName: "alert_message_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Scheduled digest send
      n("7", "hitApi", "Telegram API: Send daily digest batch", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.telegram.org/bot{telegram_bot_token}/sendMessage",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "chat_id", value: "{telegram_channel_id}" },
            {
              key: "text",
              value:
                "Daily News Digest ({total_relevant} relevant articles)\\n\\n{digest_articles}",
            },
            { key: "parse_mode", value: "Markdown" },
          ],
          variables: [
            { name: "ok", customName: "digest_sent", type: "boolean" },
          ],
        },
      }),

      // 8. Alert performance logging
      n("8", "hitApi", "Log alert metrics to Sheets", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/NewsAlerts!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{total_relevant}","{breaking_count}","{deduped_count}","{digest_count}","{duplicates_removed}","{new_urls}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 259. TESTIMONIAL PERMISSION REQUEST (7 nodes)
  // ===================================================================

  "Testimonial Permission Request": {
    summary:
      "On a schedule, identifies satisfied customers from recent high-NPS or 5-star feedback, filters out exclusions and recently contacted customers, uses AI to personalize consent requests, sends the consent email, waits for responses, logs consent status to Sheets, and generates a marketing team report.",
    nodes: [
      trigger("Weekly satisfied customer harvest", "schedule", {
        schedule: "Every Wednesday at 11:00 AM UTC",
        howItStarts:
          "Runs weekly to identify recently satisfied customers who gave high ratings and request testimonial consent",
        setup:
          "Configure CRM or feedback tool API credentials. Set NPS/rating thresholds in the filter node. Maintain an exclusion list in Google Sheets for customers who have already been asked or opted out.",
        inputFields: [],
      }),

      // 2. Filter exclusions
      n("2", "runCode", "Filter exclusions and recently contacted", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Filter satisfied customers against exclusion list
const customers = JSON.parse(inputs.satisfied_customers || '[]');
const exclusions = (inputs.exclusion_list || '').split(',').map(e => e.trim().toLowerCase());
const recentlyAsked = (inputs.recently_asked || '').split(',').map(e => e.trim().toLowerCase());

const eligible = customers.filter(c => {
  const email = (c.email || '').toLowerCase();
  if (exclusions.includes(email)) return false;
  if (recentlyAsked.includes(email)) return false;
  // Must have rating >= 4 or NPS >= 8
  const rating = c.rating || c.nps || 0;
  if (rating < 4) return false;
  return true;
});

const eligibleCount = eligible.length;
const filteredOut = customers.length - eligibleCount;

return {
  eligible_customers: JSON.stringify(eligible),
  eligible_count: eligibleCount,
  filtered_out: filteredOut,
  has_eligible: eligibleCount > 0 ? 'true' : 'false',
  top_customer_email: eligible.length > 0 ? eligible[0].email : '',
  top_customer_name: eligible.length > 0 ? (eligible[0].name || 'Valued Customer') : '',
  top_customer_feedback: eligible.length > 0 ? (eligible[0].feedback || eligible[0].comment || '') : '',
};`,
          inputVariables: [
            { name: "satisfied_customers", type: "string" },
            { name: "exclusion_list", type: "string" },
            { name: "recently_asked", type: "string" },
          ],
          outputVariables: [
            { name: "eligible_customers", type: "string" },
            { name: "eligible_count", type: "number" },
            { name: "filtered_out", type: "number" },
            { name: "has_eligible", type: "string" },
            { name: "top_customer_email", type: "string" },
            { name: "top_customer_name", type: "string" },
            { name: "top_customer_feedback", type: "string" },
          ],
        },
      }),

      // 3. AI personalized consent request
      aiNode(
        "3",
        "AI personalize consent request messaging",
        520,
        "You are a customer success writer. Craft a warm, personalized consent request email referencing the customer's specific feedback. Make it feel personal, not templated. Include the exact quote you want to feature. Return JSON: {subject_line: string, email_body: string, featured_quote: string, personalization_notes: string}",
        "Customer: {top_customer_name}\\nEmail: {top_customer_email}\\nTheir feedback: {top_customer_feedback}\\nProduct: {product_name}",
        [
          { field: "subject_line", as: "consent_subject", type: "string" },
          { field: "email_body", as: "consent_body", type: "string" },
          { field: "featured_quote", as: "featured_quote", type: "string" },
          {
            field: "personalization_notes",
            as: "personalization_notes",
            type: "string",
          },
        ],
      ),

      // 4. Send consent email
      emailNode(
        "4",
        "Send personalized consent request email",
        760,
        "{top_customer_email}",
        "{consent_subject}",
        "{consent_body}",
      ),

      // 5. Response tracking delay
      n("5", "delayAction", "Wait 5 days for consent response", 1000, {
        delayAction: { time: 5, timeFormat: "days" },
      }),

      // 6. Consent status logging
      n("6", "hitApi", "Log consent request status to Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/TestimonialConsent!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{top_customer_email}","{top_customer_name}","{featured_quote}","{consent_subject}","sent","pending"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),

      // 7. Marketing team report
      slackNode(
        "7",
        "Report testimonial pipeline to marketing",
        1480,
        'Testimonial Consent Pipeline Update\\n\\nEligible customers this week: {eligible_count}\\nFiltered out (exclusions): {filtered_out}\\nConsent requests sent: {eligible_count}\\n\\nLatest request: {top_customer_name} - \\"{featured_quote}\\"\\nStatus: Awaiting response',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 260. TWILIO SMS TWO-WAY CONVERSATION BOT (9 nodes)
  // ===================================================================

  "Twilio SMS Two-Way Conversation Bot": {
    summary:
      "Receives inbound SMS via Twilio webhook, loads conversation state, checks compliance keywords (STOP/HELP), classifies intent with AI, generates contextual replies, sends via Twilio, escalates complex issues to Zendesk, updates conversation state, and logs for QA.",
    nodes: [
      trigger("Twilio inbound SMS webhook", "webhook_app", {
        source: "Twilio",
        event: "sms.inbound",
        howItStarts: "An inbound SMS is received on the Twilio phone number",
        setup:
          "In Twilio Console -> Phone Numbers -> Configure -> Messaging, set the webhook URL for incoming messages to this workflow endpoint. Ensure Twilio account has SMS capabilities.",
        inputFields: [],
      }),

      // 2. Load conversation state
      n("2", "runCode", "Load conversation state and parse SMS", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Parse Twilio webhook and load conversation context
const payload = JSON.parse(inputs.webhook_payload || '{}');
const from = payload.From || '';
const to = payload.To || '';
const body = (payload.Body || '').trim();
const messageSid = payload.MessageSid || '';
const numMedia = parseInt(payload.NumMedia || '0', 10);

// Check for compliance keywords
const upperBody = body.toUpperCase();
const isStop = upperBody === 'STOP' || upperBody === 'UNSUBSCRIBE' || upperBody === 'CANCEL';
const isHelp = upperBody === 'HELP' || upperBody === 'INFO';
const isCompliance = isStop || isHelp;

const sessionKey = 'sms_conv:' + from.replace(/[^0-9]/g, '');

return {
  sender_phone: from,
  receiver_phone: to,
  sms_body: body,
  message_sid: messageSid,
  has_media: numMedia > 0 ? 'true' : 'false',
  is_compliance_keyword: isCompliance ? 'true' : 'false',
  compliance_type: isStop ? 'stop' : isHelp ? 'help' : 'none',
  session_key: sessionKey,
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "sender_phone", type: "string" },
            { name: "receiver_phone", type: "string" },
            { name: "sms_body", type: "string" },
            { name: "message_sid", type: "string" },
            { name: "is_compliance_keyword", type: "string" },
            { name: "compliance_type", type: "string" },
            { name: "session_key", type: "string" },
          ],
        },
      }),

      // 3. Compliance keyword check
      n("3", "condition", "Is compliance keyword (STOP/HELP)?", 520, {
        conditionDetails: {
          conditions: [
            {
              field: "is_compliance_keyword",
              operator: "equals",
              value: "false",
            },
          ],
        },
      }),

      // 4. AI intent classification
      aiNode(
        "4",
        "AI classify intent and sentiment",
        760,
        "You are an SMS conversation bot. Classify the inbound message intent (question, complaint, order_status, appointment, feedback, general_chat, escalation_needed). Also assess sentiment (positive, neutral, negative, angry). If the message requires human intervention, flag needs_escalation as true. Return JSON: {intent: string, sentiment: string, needs_escalation: boolean, confidence: number, topic: string, escalation_reason: string}",
        "Phone: {sender_phone}\\nMessage: {sms_body}\\nConversation history: {conversation_history}",
        [
          { field: "intent", as: "sms_intent", type: "string" },
          { field: "sentiment", as: "sms_sentiment", type: "string" },
          { field: "needs_escalation", as: "needs_escalation", type: "string" },
          { field: "confidence", as: "intent_confidence", type: "number" },
          { field: "topic", as: "sms_topic", type: "string" },
          {
            field: "escalation_reason",
            as: "escalation_reason",
            type: "string",
          },
        ],
      ),

      // 5. Generate reply
      aiNode(
        "5",
        "AI generate conversational SMS reply",
        1000,
        "You are an SMS customer service bot. Generate a helpful, concise reply (under 160 chars ideally, max 320 chars for multi-part). Match the customer's tone. Be warm and professional. If you cannot help, suggest calling support. Return JSON: {reply_text: string, reply_length: number, is_multi_part: boolean}",
        "Intent: {sms_intent}\\nSentiment: {sms_sentiment}\\nCustomer message: {sms_body}\\nTopic: {sms_topic}\\nHistory: {conversation_history}",
        [
          { field: "reply_text", as: "sms_reply", type: "string" },
          { field: "reply_length", as: "reply_length", type: "number" },
          { field: "is_multi_part", as: "is_multi_part", type: "string" },
        ],
      ),

      // 6. Send SMS reply via Twilio
      n("6", "hitApi", "Twilio API: Send SMS reply", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twilio.com/2010-04-01/Accounts/{twilio_account_sid}/Messages.json",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth_base64}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "To", value: "{sender_phone}" },
            { key: "From", value: "{receiver_phone}" },
            { key: "Body", value: "{sms_reply}" },
          ],
          variables: [
            { name: "sid", customName: "reply_message_sid", type: "string" },
            { name: "status", customName: "reply_status", type: "string" },
          ],
        },
      }),

      // 7. Zendesk escalation ticket
      n("7", "hitApi", "Create Zendesk escalation ticket", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "ticket",
              value:
                '{"subject":"SMS Escalation: {sms_topic} from {sender_phone}","description":"Automated escalation from SMS bot.\\n\\nCustomer: {sender_phone}\\nMessage: {sms_body}\\nIntent: {sms_intent}\\nSentiment: {sms_sentiment}\\nEscalation reason: {escalation_reason}","priority":"high","tags":["sms-escalation","auto-created"]}',
            },
          ],
          variables: [
            {
              name: "ticket.id",
              customName: "zendesk_ticket_id",
              type: "number",
            },
          ],
        },
      }),

      // 8. State update
      n("8", "hitApi", "Update conversation state in Redis", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "{redis_rest_url}/set/{session_key}",
          headers: [
            { key: "Authorization", value: "Bearer {redis_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "value",
              value:
                '{"last_intent":"{sms_intent}","last_sentiment":"{sms_sentiment}","last_reply":"{sms_reply}","last_message":"{sms_body}","updated":"{date}"}',
            },
            { key: "ex", value: "86400" },
          ],
          variables: [
            { name: "result", customName: "state_saved", type: "string" },
          ],
        },
      }),

      // 9. QA logging
      n("9", "hitApi", "Log conversation to QA sheet", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/SMSConversations!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{sender_phone}","{sms_body}","{sms_intent}","{sms_sentiment}","{sms_reply}","{needs_escalation}","{reply_status}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "qa_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "9", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7"),
      e("6", "8"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 261. TWILIO SMS TO GOOGLE SHEET LOGGER (7 nodes — spec says 6 but using 7 for proper flow)
  // ===================================================================

  "Twilio SMS to Google Sheet Logger": {
    summary:
      "Receives inbound SMS via Twilio webhook, uses AI to parse and categorize content, deduplicates against recent entries, appends structured data to Google Sheets, checks for urgent content, and alerts support via Slack when urgent messages arrive.",
    nodes: [
      trigger("Twilio inbound SMS webhook", "webhook_app", {
        source: "Twilio",
        event: "sms.inbound",
        howItStarts:
          "An inbound SMS is received on the Twilio number and needs to be logged",
        setup:
          "Configure Twilio webhook URL for incoming messages. Set up a Google Sheet with columns: Date, Phone, Category, Parsed Content, Original Text, Urgency.",
        inputFields: [],
      }),

      // 2. AI content parsing
      aiNode(
        "2",
        "AI parse and categorize SMS content",
        280,
        "You are an SMS content parser. Extract structured data from the inbound SMS: category (order, inquiry, feedback, complaint, appointment, other), parsed key-value pairs, urgency level (urgent, normal, low), and a clean summary. Return JSON: {category: string, parsed_data: string, urgency: string, clean_summary: string, sender_phone: string, contains_pii: boolean}",
        "{webhook_payload}",
        [
          { field: "category", as: "sms_category", type: "string" },
          { field: "parsed_data", as: "parsed_data", type: "string" },
          { field: "urgency", as: "sms_urgency", type: "string" },
          { field: "clean_summary", as: "clean_summary", type: "string" },
          { field: "sender_phone", as: "sender_phone", type: "string" },
          { field: "contains_pii", as: "contains_pii", type: "string" },
        ],
      ),

      // 3. Dedup check
      n("3", "runCode", "Deduplicate against recent SMS entries", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Generate a dedup key from phone + content hash to prevent double-logging
const phone = inputs.sender_phone || '';
const summary = inputs.clean_summary || '';
const category = inputs.sms_category || '';

// Simple hash for dedup
const dedupKey = phone.replace(/[^0-9]/g, '') + '_' + summary.substring(0, 30).replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
const timestamp = new Date().toISOString();

return {
  dedup_key: dedupKey,
  log_timestamp: timestamp,
  is_duplicate: 'false', // In production, check against stored keys
  normalized_phone: phone.replace(/[^0-9+]/g, ''),
};`,
          inputVariables: [
            { name: "sender_phone", type: "string" },
            { name: "clean_summary", type: "string" },
            { name: "sms_category", type: "string" },
          ],
          outputVariables: [
            { name: "dedup_key", type: "string" },
            { name: "log_timestamp", type: "string" },
            { name: "is_duplicate", type: "string" },
            { name: "normalized_phone", type: "string" },
          ],
        },
      }),

      // 4. Google Sheets append
      n("4", "hitApi", "Append parsed SMS to Google Sheets", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/SMSLog!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{log_timestamp}","{normalized_phone}","{sms_category}","{parsed_data}","{clean_summary}","{sms_urgency}","{dedup_key}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_appended",
              type: "number",
            },
          ],
        },
      }),

      // 5. Urgent content condition
      n("5", "condition", "Is SMS content urgent?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "sms_urgency", operator: "equals", value: "urgent" },
          ],
        },
      }),

      // 6. Support Slack alert
      slackNode(
        "6",
        "Alert support team about urgent SMS",
        1240,
        "URGENT SMS Received\\n\\nFrom: {normalized_phone}\\nCategory: {sms_category}\\nSummary: {clean_summary}\\nParsed data: {parsed_data}\\n\\nPlease respond within SLA.",
      ),

      // 7. Confirmation log
      n("7", "hitApi", "Log processing status to Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ProcessingLog!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{log_timestamp}","{normalized_phone}","{sms_category}","{sms_urgency}","processed","{dedup_key}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "status_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 262. VOICE MEMO TO TEXT CONVERTER (7 nodes)
  // ===================================================================

  "Voice Memo to Text Converter": {
    summary:
      "Accepts audio file uploads, validates format and duration, transcribes using OpenAI Whisper, uses AI to clean up and summarize the transcript, stores the output in Google Drive, notifies the requester via email, and logs the conversion.",
    nodes: [
      trigger("Audio upload webhook", "webhook_app", {
        source: "Slack / Web Upload / Mobile App",
        event: "file.uploaded",
        howItStarts:
          "An audio file (voice memo) is uploaded for transcription via webhook or file upload endpoint",
        setup:
          "Configure the upload endpoint to accept audio files (m4a, mp3, wav, ogg, webm). Set max file size to 25MB (Whisper API limit). Ensure OpenAI API key has Whisper access.",
        inputFields: [],
      }),

      // 2. Format validation condition
      n("2", "condition", "Audio format is supported?", 280, {
        conditionDetails: {
          conditions: [
            { field: "file_format_valid", operator: "equals", value: "true" },
          ],
        },
      }),

      // 3. Whisper transcription
      n("3", "hitApi", "OpenAI Whisper: Transcribe audio", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/audio/transcriptions",
          headers: [{ key: "Authorization", value: "Bearer {openai_key}" }],
          body: [
            { key: "model", value: "whisper-1" },
            { key: "file", value: "{audio_file_url}" },
            { key: "response_format", value: "verbose_json" },
            { key: "language", value: "en" },
          ],
          variables: [
            { name: "text", customName: "raw_transcript", type: "string" },
            {
              name: "duration",
              customName: "audio_duration_seconds",
              type: "number",
            },
            {
              name: "language",
              customName: "detected_language",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI cleanup + summary
      aiNode(
        "4",
        "AI clean transcript and generate summary",
        760,
        "You are a transcription editor. Clean up the raw speech-to-text output: fix punctuation, remove filler words (um, uh, like), add paragraph breaks at topic changes, identify speakers if multiple voices. Then generate a concise summary of key points. Return JSON: {cleaned_transcript: string, summary: string, key_points: [string], word_count: number, topic: string, speaker_count: number, action_items: [string]}",
        "Raw transcript ({audio_duration_seconds}s, {detected_language}):\\n{raw_transcript}",
        [
          {
            field: "cleaned_transcript",
            as: "cleaned_transcript",
            type: "string",
          },
          { field: "summary", as: "transcript_summary", type: "string" },
          { field: "key_points", as: "key_points", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
          { field: "topic", as: "transcript_topic", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
        ],
      ),

      // 5. Google Drive storage
      n("5", "hitApi", "Store transcript in Google Drive", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "name",
              value: "Transcript - {transcript_topic} - {date}.txt",
            },
            { key: "parents", value: '["{transcripts_folder_id}"]' },
            { key: "mimeType", value: "text/plain" },
            {
              key: "description",
              value:
                "Voice memo transcript | Duration: {audio_duration_seconds}s | Words: {word_count} | Topic: {transcript_topic}",
            },
          ],
          variables: [
            { name: "id", customName: "drive_file_id", type: "string" },
            {
              name: "webViewLink",
              customName: "drive_view_link",
              type: "string",
            },
          ],
        },
      }),

      // 6. Notification email
      emailNode(
        "6",
        "Notify requester of completed transcription",
        1240,
        "{requester_email}",
        "Voice memo transcribed: {transcript_topic}",
        "Hi,\\n\\nYour voice memo has been transcribed successfully.\\n\\nTopic: {transcript_topic}\\nDuration: {audio_duration_seconds} seconds\\nWord count: {word_count}\\n\\nSummary:\\n{transcript_summary}\\n\\nKey points:\\n{key_points}\\n\\nAction items:\\n{action_items}\\n\\nFull transcript: {drive_view_link}\\n\\nBest regards",
      ),

      // 7. Conversion logging
      n("7", "hitApi", "Log conversion to tracking sheet", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Transcriptions!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{requester_email}","{transcript_topic}","{audio_duration_seconds}","{word_count}","{detected_language}","{drive_file_id}","completed"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3", "output_1"),
      e("2", "7", "output_2"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 263. VOICEBOT TO HUMAN HANDOFF (8 nodes)
  // ===================================================================

  "Voicebot to Human Handoff": {
    summary:
      "Receives an escalation webhook from a voicebot, loads the full transcript and caller context, checks agent availability, performs a warm transfer with AI-generated summary for available agents or schedules a callback when none are available, creates a Zendesk ticket, alerts support via Slack, and logs handoff metrics.",
    nodes: [
      trigger("Voicebot escalation webhook", "webhook_app", {
        source: "Voicebot Platform (Dialogflow / Amazon Connect)",
        event: "escalation.requested",
        howItStarts:
          "The voicebot determines it cannot resolve the caller's issue and requests a human handoff",
        setup:
          "Configure the voicebot platform to POST escalation events (call_id, caller_phone, transcript, intent, sentiment) to this workflow URL. Integrate with your contact center queue API.",
        inputFields: [],
      }),

      // 2. Load transcript + caller context
      aiNode(
        "2",
        "AI extract caller context from escalation",
        280,
        "Parse this voicebot escalation payload. Extract the caller phone, call ID, full conversation transcript, detected intent, sentiment, caller name if identified, account number if mentioned, and reason for escalation. Return JSON: {caller_phone: string, call_id: string, transcript: string, intent: string, sentiment: string, caller_name: string, account_number: string, escalation_reason: string, call_duration_seconds: number, urgency: string}",
        "{webhook_payload}",
        [
          { field: "caller_phone", as: "caller_phone", type: "string" },
          { field: "call_id", as: "call_id", type: "string" },
          { field: "transcript", as: "call_transcript", type: "string" },
          { field: "intent", as: "caller_intent", type: "string" },
          { field: "sentiment", as: "caller_sentiment", type: "string" },
          { field: "caller_name", as: "caller_name", type: "string" },
          {
            field: "escalation_reason",
            as: "escalation_reason",
            type: "string",
          },
          { field: "urgency", as: "call_urgency", type: "string" },
        ],
      ),

      // 3. Agent availability check
      n("3", "condition", "Agents available for transfer?", 520, {
        conditionDetails: {
          conditions: [
            { field: "agents_available", operator: "greater_than", value: "0" },
          ],
        },
      }),

      // 4. Warm transfer with AI summary (True path — agents available)
      aiNode(
        "4",
        "AI generate agent briefing for warm transfer",
        760,
        "Generate a concise agent briefing for a live handoff. Include: caller identity, issue summary (2-3 sentences), sentiment/urgency, what the bot already attempted, suggested resolution path. Keep it scannable. Return JSON: {agent_briefing: string, suggested_resolution: string, caller_history_note: string, priority_level: string}",
        "Caller: {caller_name} ({caller_phone})\\nIntent: {caller_intent}\\nSentiment: {caller_sentiment}\\nEscalation reason: {escalation_reason}\\nTranscript:\\n{call_transcript}",
        [
          { field: "agent_briefing", as: "agent_briefing", type: "string" },
          {
            field: "suggested_resolution",
            as: "suggested_resolution",
            type: "string",
          },
          { field: "priority_level", as: "priority_level", type: "string" },
        ],
      ),

      // 5. Callback scheduling fallback (False path — no agents)
      n(
        "5",
        "hitApi",
        "Schedule callback via contact center API",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.{contact_center_domain}/v1/callbacks",
            headers: [
              { key: "Authorization", value: "Bearer {contact_center_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "phone", value: "{caller_phone}" },
              { key: "caller_name", value: "{caller_name}" },
              { key: "reason", value: "{escalation_reason}" },
              { key: "priority", value: "{call_urgency}" },
              {
                key: "notes",
                value:
                  "Voicebot escalation: {caller_intent}. Caller sentiment: {caller_sentiment}.",
              },
            ],
            variables: [
              {
                name: "callback_id",
                customName: "callback_id",
                type: "string",
              },
              {
                name: "estimated_wait",
                customName: "estimated_wait_minutes",
                type: "number",
              },
            ],
          },
        },
        460,
      ),

      // 6. Zendesk ticket creation
      n("6", "hitApi", "Create Zendesk support ticket", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "ticket",
              value:
                '{"subject":"Voice Escalation: {caller_intent} - {caller_name}","description":"Call ID: {call_id}\\nCaller: {caller_name} ({caller_phone})\\nIntent: {caller_intent}\\nSentiment: {caller_sentiment}\\nEscalation: {escalation_reason}\\n\\nAgent Briefing: {agent_briefing}\\n\\nTranscript:\\n{call_transcript}","priority":"{priority_level}","tags":["voice-escalation","voicebot-handoff"]}',
            },
          ],
          variables: [
            {
              name: "ticket.id",
              customName: "zendesk_ticket_id",
              type: "number",
            },
            {
              name: "ticket.url",
              customName: "zendesk_ticket_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. Support Slack alert
      slackNode(
        "7",
        "Alert support team in Slack",
        1240,
        "Voice Escalation Alert\\n\\nCaller: {caller_name} ({caller_phone})\\nIntent: {caller_intent} | Sentiment: {caller_sentiment}\\nReason: {escalation_reason}\\nPriority: {priority_level}\\nZendesk: #{zendesk_ticket_id}\\n\\nBriefing: {agent_briefing}",
      ),

      // 8. Handoff metrics logging
      n("8", "hitApi", "Log handoff metrics to Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/HandoffMetrics!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{call_id}","{caller_phone}","{caller_intent}","{caller_sentiment}","{escalation_reason}","{priority_level}","{zendesk_ticket_id}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "metrics_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 264. WHATSAPP BROADCAST LIST MANAGER (8 nodes)
  // ===================================================================

  "WhatsApp Broadcast List Manager": {
    summary:
      "Receives contact update webhooks, validates phone numbers, verifies consent status, applies segment logic, updates the master broadcast list, syncs the cache, alerts ops on anomalies via Slack, and maintains a consent audit log in Sheets.",
    nodes: [
      trigger("Contact update webhook", "webhook_app", {
        source: "CRM / Signup Form / Import Tool",
        event: "contact.updated",
        howItStarts:
          "A contact record is created or updated with WhatsApp-eligible phone number",
        setup:
          "Configure your CRM or contact management system to POST contact changes (phone, name, consent_status, segments, opt_in_date) to this workflow URL. Ensure WhatsApp Business API is configured.",
        inputFields: [],
      }),

      // 2. Phone validation
      n("2", "runCode", "Validate and normalize phone number", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Validate and normalize phone number for WhatsApp
const payload = JSON.parse(inputs.webhook_payload || '{}');
const rawPhone = payload.phone || payload.mobile || payload.whatsapp_number || '';
const name = payload.name || payload.full_name || '';
const email = payload.email || '';
const consentStatus = payload.consent_status || payload.opt_in || 'unknown';
const segments = payload.segments || payload.tags || [];
const optInDate = payload.opt_in_date || '';
const contactId = payload.id || payload.contact_id || '';

// Normalize phone: strip spaces, ensure + prefix, validate length
let normalized = rawPhone.replace(/[\\s\\-()]/g, '');
if (!normalized.startsWith('+')) normalized = '+' + normalized;
const isValidLength = normalized.length >= 10 && normalized.length <= 16;
const hasCountryCode = /^\\+[1-9]\\d{1,3}/.test(normalized);
const phoneValid = isValidLength && hasCountryCode ? 'true' : 'false';

return {
  contact_id: contactId,
  contact_name: name,
  contact_email: email,
  normalized_phone: normalized,
  phone_valid: phoneValid,
  consent_status: consentStatus,
  segments: Array.isArray(segments) ? segments.join(',') : segments,
  opt_in_date: optInDate,
  raw_phone: rawPhone,
};`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "contact_id", type: "string" },
            { name: "contact_name", type: "string" },
            { name: "contact_email", type: "string" },
            { name: "normalized_phone", type: "string" },
            { name: "phone_valid", type: "string" },
            { name: "consent_status", type: "string" },
            { name: "segments", type: "string" },
            { name: "opt_in_date", type: "string" },
          ],
        },
      }),

      // 3. Consent verification condition
      n("3", "condition", "Phone valid and consent given?", 520, {
        conditionDetails: {
          conditions: [
            { field: "phone_valid", operator: "equals", value: "true" },
          ],
        },
      }),

      // 4. Segment logic application
      n("4", "runCode", "Apply segment logic for broadcast lists", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Determine which broadcast lists this contact belongs to
const segments = (inputs.segments || '').split(',').map(s => s.trim()).filter(Boolean);
const consent = inputs.consent_status || '';
const phone = inputs.normalized_phone || '';

// Map segments to broadcast list IDs
const segmentToList = {
  'vip': 'list_vip',
  'new_customer': 'list_onboarding',
  'active': 'list_promotions',
  'churning': 'list_retention',
  'enterprise': 'list_enterprise',
};

const assignedLists = segments
  .map(s => segmentToList[s.toLowerCase()])
  .filter(Boolean);

// Always add to master list if consented
if (consent === 'opted_in' || consent === 'active') {
  assignedLists.unshift('list_master');
}

const listCount = assignedLists.length;
const isAnomaly = listCount === 0 && consent === 'opted_in' ? 'true' : 'false';

return {
  assigned_lists: assignedLists.join(','),
  list_count: listCount,
  is_anomaly: isAnomaly,
  consent_verified: consent === 'opted_in' || consent === 'active' ? 'true' : 'false',
};`,
          inputVariables: [
            { name: "segments", type: "string" },
            { name: "consent_status", type: "string" },
            { name: "normalized_phone", type: "string" },
          ],
          outputVariables: [
            { name: "assigned_lists", type: "string" },
            { name: "list_count", type: "number" },
            { name: "is_anomaly", type: "string" },
            { name: "consent_verified", type: "string" },
          ],
        },
      }),

      // 5. Master list update
      n("5", "hitApi", "Update WhatsApp master broadcast list", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{whatsapp_business_id}/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {whatsapp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "contacts",
              value:
                '[{"wa_id":"{normalized_phone}","profile":{"name":"{contact_name}"},"tags":"{assigned_lists}"}]',
            },
          ],
          variables: [
            {
              name: "contacts[0].status",
              customName: "update_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. Cache sync
      n("6", "hitApi", "Sync contact to Redis cache", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "{redis_rest_url}/set/wa_contact:{normalized_phone}",
          headers: [
            { key: "Authorization", value: "Bearer {redis_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "value",
              value:
                '{"name":"{contact_name}","phone":"{normalized_phone}","lists":"{assigned_lists}","consent":"{consent_status}","updated":"{date}"}',
            },
            { key: "ex", value: "604800" },
          ],
          variables: [
            { name: "result", customName: "cache_synced", type: "string" },
          ],
        },
      }),

      // 7. Ops anomaly alert
      slackNode(
        "7",
        "Alert ops on broadcast list anomalies",
        1480,
        "WhatsApp Broadcast List Alert\\n\\nContact: {contact_name} ({normalized_phone})\\nConsent: {consent_status} | Lists: {list_count}\\nSegments: {segments}\\nAssigned lists: {assigned_lists}\\nAnomaly detected: {is_anomaly}\\nUpdate status: {update_status}",
      ),

      // 8. Consent audit log
      n("8", "hitApi", "Log consent audit trail to Sheets", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ConsentAudit!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{contact_id}","{contact_name}","{normalized_phone}","{consent_status}","{opt_in_date}","{assigned_lists}","{update_status}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "audit_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "8", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 265. WHATSAPP CATALOG BROWSING BOT (8 nodes)
  // ===================================================================

  "WhatsApp Catalog Browsing Bot": {
    summary:
      "Receives WhatsApp messages, classifies intent with AI, queries the Shopify product catalog, checks result quality, sends interactive product cards via WhatsApp, manages cart state, generates checkout links, and escalates to human agents when needed.",
    nodes: [
      trigger("WhatsApp Business API webhook", "webhook_app", {
        source: "WhatsApp Business API",
        event: "messages",
        howItStarts:
          "A customer sends a message to the WhatsApp Business number to browse products or ask questions",
        setup:
          "Configure WhatsApp Business API webhook for incoming messages. Connect Shopify store for catalog queries. Set up Redis for cart state management.",
        inputFields: [],
      }),

      // 2. AI intent classification
      aiNode(
        "2",
        "AI classify shopping intent",
        280,
        "You are a WhatsApp shopping assistant. Classify the customer message intent: browse_catalog (looking at products), search_product (specific item search), add_to_cart (wants to add item), view_cart (check current cart), checkout (ready to buy), ask_question (product question), need_help (wants human agent). Extract search terms if applicable. Return JSON: {intent: string, search_query: string, product_id: string, quantity: number, customer_phone: string, message_text: string, needs_human: boolean}",
        "{webhook_payload}",
        [
          { field: "intent", as: "shopping_intent", type: "string" },
          { field: "search_query", as: "search_query", type: "string" },
          { field: "product_id", as: "requested_product_id", type: "string" },
          { field: "quantity", as: "requested_quantity", type: "number" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "needs_human", as: "needs_human", type: "string" },
        ],
      ),

      // 3. Shopify catalog query
      n("3", "hitApi", "Shopify API: Search product catalog", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/products.json?title={search_query}&status=active&limit=5",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_access_token}" },
          ],
          body: [],
          variables: [
            { name: "products", customName: "product_results", type: "string" },
          ],
        },
      }),

      // 4. Result quality condition
      n("4", "condition", "Product results found?", 760, {
        conditionDetails: {
          conditions: [
            { field: "product_results", operator: "not_equals", value: "[]" },
          ],
        },
      }),

      // 5. WhatsApp interactive product cards
      n("5", "whatsApp", "Send product cards via WhatsApp", 1000, {
        whatsappDetails: {
          templateName: "product_catalog",
          lang: "en",
          to: "{customer_phone}",
          smsBody:
            "Here are the products I found for '{search_query}':\\n\\n{product_results}\\n\\nReply with the product number to add to cart, or type 'more' to see more options.",
        },
      }),

      // 6. Cart state management
      n("6", "runCode", "Manage shopping cart state", 1240, {
        codeDetails: {
          language: "javascript",
          code: `// Manage cart state for the customer
const intent = inputs.shopping_intent || '';
const phone = inputs.customer_phone || '';
const productId = inputs.requested_product_id || '';
const quantity = parseInt(inputs.requested_quantity || '1', 10);
const existingCart = JSON.parse(inputs.existing_cart || '{"items":[]}');

let cart = existingCart;
let cartAction = 'none';

if (intent === 'add_to_cart' && productId) {
  const existingItem = cart.items.find(i => i.product_id === productId);
  if (existingItem) {
    existingItem.quantity += quantity;
  } else {
    cart.items.push({ product_id: productId, quantity: quantity });
  }
  cartAction = 'item_added';
}

const itemCount = cart.items.reduce((sum, i) => sum + i.quantity, 0);
const cartSummary = cart.items.map(i => i.product_id + ' x' + i.quantity).join(', ');
const readyForCheckout = itemCount > 0 ? 'true' : 'false';

return {
  updated_cart: JSON.stringify(cart),
  cart_action: cartAction,
  item_count: itemCount,
  cart_summary: cartSummary || 'Cart is empty',
  ready_for_checkout: readyForCheckout,
};`,
          inputVariables: [
            { name: "shopping_intent", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "requested_product_id", type: "string" },
            { name: "requested_quantity", type: "number" },
            { name: "existing_cart", type: "string" },
          ],
          outputVariables: [
            { name: "updated_cart", type: "string" },
            { name: "cart_action", type: "string" },
            { name: "item_count", type: "number" },
            { name: "cart_summary", type: "string" },
            { name: "ready_for_checkout", type: "string" },
          ],
        },
      }),

      // 7. Checkout link generation
      n("7", "hitApi", "Shopify API: Generate checkout link", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/checkouts.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "checkout",
              value: '{"line_items":{updated_cart},"email":"{customer_email}"}',
            },
          ],
          variables: [
            {
              name: "checkout.web_url",
              customName: "checkout_url",
              type: "string",
            },
            {
              name: "checkout.token",
              customName: "checkout_token",
              type: "string",
            },
          ],
        },
      }),

      // 8. Human escalation
      n("8", "hitApi", "Escalate to human agent via WhatsApp API", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{whatsapp_phone_id}/messages",
          headers: [
            { key: "Authorization", value: "Bearer {whatsapp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "messaging_product", value: "whatsapp" },
            { key: "to", value: "{customer_phone}" },
            { key: "type", value: "text" },
            {
              key: "text",
              value:
                '{"body":"I am connecting you with a team member who can help you directly. They will have your conversation history and cart ({cart_summary}). Please hold on!"}',
            },
          ],
          variables: [
            {
              name: "messages[0].id",
              customName: "escalation_message_id",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 266. WHATSAPP MARKETING CAMPAIGN SENDER (8 nodes)
  // ===================================================================

  "WhatsApp Marketing Campaign Sender": {
    summary:
      "Launches a WhatsApp marketing campaign by loading the recipient list with personalization, validating consent and frequency caps, checking template compliance, sending via WhatsApp Business API with rate control, monitoring delivery status, logging campaign metrics to Sheets, and reporting results to marketing ops in Slack.",
    nodes: [
      trigger("Campaign launch trigger", "webhook_app", {
        source: "Marketing Automation / Manual Trigger",
        event: "campaign.launch",
        howItStarts:
          "A marketing team member triggers a WhatsApp campaign launch via the campaign dashboard or API",
        setup:
          "Configure WhatsApp Business API with approved message templates. Set up recipient lists in CRM with consent flags. Define frequency caps in the validation node.",
        inputFields: [],
      }),

      // 2. Recipient list + personalization load
      n("2", "hitApi", "Load recipient list with personalization data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.{crm_domain}/v1/lists/{campaign_list_id}/contacts?consent=opted_in&fields=phone,name,segment,last_purchase,loyalty_tier",
          headers: [
            { key: "Authorization", value: "Bearer {crm_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "contacts", customName: "recipient_list", type: "string" },
            {
              name: "total_count",
              customName: "total_recipients",
              type: "number",
            },
          ],
        },
      }),

      // 3. Consent + frequency validation
      n("3", "runCode", "Validate consent and enforce frequency caps", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Validate consent and enforce frequency caps (max 2 campaigns per week per contact)
const recipients = JSON.parse(inputs.recipient_list || '[]');
const maxPerWeek = 2;
const now = Date.now();
const oneWeekMs = 7 * 24 * 60 * 60 * 1000;

const validated = recipients.filter(r => {
  // Must have valid consent
  if (r.consent !== 'opted_in' && r.consent !== true) return false;
  // Must have valid phone
  if (!r.phone || r.phone.length < 10) return false;
  // Frequency cap check
  const lastSent = r.last_campaign_date ? new Date(r.last_campaign_date).getTime() : 0;
  const campaignsThisWeek = r.campaigns_this_week || 0;
  if (campaignsThisWeek >= maxPerWeek) return false;
  return true;
});

const validCount = validated.length;
const rejectedCount = recipients.length - validCount;
const consentViolations = recipients.filter(r => r.consent !== 'opted_in' && r.consent !== true).length;
const frequencyCapped = rejectedCount - consentViolations;

return {
  validated_recipients: JSON.stringify(validated),
  valid_count: validCount,
  rejected_count: rejectedCount,
  consent_violations: consentViolations,
  frequency_capped: frequencyCapped,
  compliance_pass: consentViolations === 0 ? 'true' : 'false',
};`,
          inputVariables: [{ name: "recipient_list", type: "string" }],
          outputVariables: [
            { name: "validated_recipients", type: "string" },
            { name: "valid_count", type: "number" },
            { name: "rejected_count", type: "number" },
            { name: "consent_violations", type: "number" },
            { name: "frequency_capped", type: "number" },
            { name: "compliance_pass", type: "string" },
          ],
        },
      }),

      // 4. Template compliance check
      n("4", "condition", "Template approved and compliance passes?", 760, {
        conditionDetails: {
          conditions: [
            { field: "compliance_pass", operator: "equals", value: "true" },
          ],
        },
      }),

      // 5. WhatsApp API send with rate control
      n("5", "hitApi", "WhatsApp Business API: Send campaign messages", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{whatsapp_phone_id}/messages",
          headers: [
            { key: "Authorization", value: "Bearer {whatsapp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "messaging_product", value: "whatsapp" },
            { key: "type", value: "template" },
            {
              key: "template",
              value:
                '{"name":"{template_name}","language":{"code":"en"},"components":[{"type":"body","parameters":[{"type":"text","text":"{personalized_greeting}"},{"type":"text","text":"{campaign_offer}"},{"type":"text","text":"{cta_link}"}]}]}',
            },
          ],
          variables: [
            {
              name: "messages[0].id",
              customName: "batch_message_id",
              type: "string",
            },
            {
              name: "messages[0].message_status",
              customName: "send_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. Delivery monitoring
      n("6", "hitApi", "Monitor delivery status via WhatsApp API", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v18.0/{batch_message_id}",
          headers: [{ key: "Authorization", value: "Bearer {whatsapp_token}" }],
          body: [],
          variables: [
            { name: "status", customName: "delivery_status", type: "string" },
            {
              name: "pricing.billable",
              customName: "billable_messages",
              type: "number",
            },
          ],
        },
      }),

      // 7. Campaign metrics to Sheets
      n("7", "hitApi", "Log campaign metrics to Google Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/WhatsAppCampaigns!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{template_name}","{total_recipients}","{valid_count}","{rejected_count}","{consent_violations}","{frequency_capped}","{delivery_status}","{billable_messages}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "metrics_logged",
              type: "number",
            },
          ],
        },
      }),

      // 8. Marketing ops Slack report
      slackNode(
        "8",
        "Report campaign results to marketing ops",
        1720,
        "WhatsApp Campaign Report\\n\\nTemplate: {template_name}\\nTotal recipients: {total_recipients}\\nSent: {valid_count} | Rejected: {rejected_count}\\nConsent violations: {consent_violations} | Frequency capped: {frequency_capped}\\nDelivery status: {delivery_status}\\nBillable: {billable_messages}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 267. ZOOM RECORDING TO GOOGLE DRIVE (7 nodes)
  // ===================================================================

  "Zoom Recording to Google Drive": {
    summary:
      "Listens for Zoom recording completed webhooks, securely downloads the recording, filters by minimum duration, uploads to Google Drive, uses AI to generate a transcript summary with key topics, logs to an index sheet, and notifies the team in Slack with a sharing link.",
    nodes: [
      trigger("Zoom recording.completed webhook", "webhook_app", {
        source: "Zoom Marketplace App",
        event: "recording.completed",
        howItStarts:
          "A Zoom meeting ends and the cloud recording processing completes",
        setup:
          "In Zoom Marketplace -> App -> Event Subscriptions, subscribe to recording.completed events. Set the notification URL to this workflow. Ensure the Zoom app has recording:read scope.",
        inputFields: [],
      }),

      // 2. Secure download
      n("2", "hitApi", "Zoom API: Download recording securely", 280, {
        hitApiDetails: {
          method: "GET",
          url: "{download_url}?access_token={zoom_jwt_token}",
          headers: [{ key: "Authorization", value: "Bearer {zoom_jwt_token}" }],
          body: [],
          variables: [
            {
              name: "content",
              customName: "recording_content",
              type: "string",
            },
            {
              name: "content-length",
              customName: "file_size_bytes",
              type: "number",
            },
          ],
        },
      }),

      // 3. Duration filter condition
      n("3", "condition", "Recording meets minimum duration (5 min)?", 520, {
        conditionDetails: {
          conditions: [
            {
              field: "recording_duration_minutes",
              operator: "greater_than",
              value: "5",
            },
          ],
        },
      }),

      // 4. Google Drive upload
      n("4", "hitApi", "Upload recording to Google Drive", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{meeting_topic} - {meeting_date}.mp4" },
            { key: "parents", value: '["{zoom_recordings_folder_id}"]' },
            { key: "mimeType", value: "video/mp4" },
            {
              key: "description",
              value:
                "Zoom recording | Meeting: {meeting_topic} | Duration: {recording_duration_minutes} min | Host: {host_email} | Participants: {participant_count}",
            },
          ],
          variables: [
            { name: "id", customName: "drive_file_id", type: "string" },
            {
              name: "webViewLink",
              customName: "drive_view_link",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI transcript + summary
      aiNode(
        "5",
        "AI generate transcript summary and key topics",
        1000,
        "You are a meeting analyst. Analyze the meeting recording transcript and generate: a concise executive summary, key discussion topics, decisions made, action items with owners, and follow-up items. Return JSON: {executive_summary: string, key_topics: [string], decisions: [string], action_items: [{task, owner, due}], follow_ups: [string], meeting_type: string, sentiment: string}",
        "Meeting: {meeting_topic}\\nDuration: {recording_duration_minutes} min\\nHost: {host_email}\\nParticipants: {participant_count}\\nTranscript: {recording_transcript}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_topics", as: "key_topics", type: "string" },
          { field: "decisions", as: "decisions_made", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "meeting_type", as: "meeting_type", type: "string" },
        ],
      ),

      // 6. Index logging to Sheets
      n("6", "hitApi", "Log recording index to Google Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ZoomRecordings!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{meeting_topic}","{host_email}","{recording_duration_minutes}","{participant_count}","{meeting_type}","{drive_file_id}","{drive_view_link}","{executive_summary}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),

      // 7. Slack sharing notification
      slackNode(
        "7",
        "Notify team of new recording in Slack",
        1480,
        "New Zoom Recording Available\\n\\nMeeting: {meeting_topic}\\nDuration: {recording_duration_minutes} min | Host: {host_email}\\nType: {meeting_type}\\n\\nSummary: {executive_summary}\\n\\nKey topics: {key_topics}\\nDecisions: {decisions_made}\\nAction items: {action_items}\\n\\nWatch: {drive_view_link}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "7", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 268. A/B TEST AUTOMATOR (8 nodes)
  // ===================================================================

  "A/B Test Automator": {
    summary:
      "Triggers when a test reaches sample threshold, pulls active test metrics, runs statistical analysis (p-value, confidence intervals), checks significance, generates AI business interpretation, updates the test registry, notifies the growth team in Slack, and suggests follow-on experiment ideas.",
    nodes: [
      trigger("Sample threshold trigger", "webhook_app", {
        source:
          "Experimentation Platform (LaunchDarkly / Optimizely / Internal)",
        event: "test.threshold_reached",
        howItStarts:
          "An A/B test reaches its pre-defined minimum sample size threshold for statistical analysis",
        setup:
          "Configure your experimentation platform to POST threshold events (test_id, variant_data, sample_sizes, primary_metric, duration) to this workflow URL. Minimum sample size should be at least 1000 per variant.",
        inputFields: [],
      }),

      // 2. Pull active test + metrics
      n("2", "hitApi", "Pull test metrics from experiment platform", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.{experiment_platform}/v1/experiments/{test_id}/results?include=metrics,variants,confidence",
          headers: [
            { key: "Authorization", value: "Bearer {experiment_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "experiment.name",
              customName: "test_name",
              type: "string",
            },
            {
              name: "experiment.variants",
              customName: "variant_data",
              type: "string",
            },
            {
              name: "experiment.metrics",
              customName: "test_metrics",
              type: "string",
            },
            {
              name: "experiment.status",
              customName: "test_status",
              type: "string",
            },
            {
              name: "experiment.started_at",
              customName: "test_start_date",
              type: "string",
            },
          ],
        },
      }),

      // 3. Statistical analysis
      n("3", "runCode", "Run statistical significance analysis", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Perform statistical analysis on A/B test results
const variants = JSON.parse(inputs.variant_data || '[]');
const metrics = JSON.parse(inputs.test_metrics || '{}');

// Extract control and treatment data
const control = variants.find(v => v.is_control) || variants[0] || {};
const treatment = variants.find(v => !v.is_control) || variants[1] || {};

const controlRate = control.conversion_rate || 0;
const treatmentRate = treatment.conversion_rate || 0;
const controlN = control.sample_size || 0;
const treatmentN = treatment.sample_size || 0;

// Calculate relative lift
const relativeLift = controlRate > 0 ? ((treatmentRate - controlRate) / controlRate * 100).toFixed(2) : 0;

// Approximate z-score for significance (simplified)
const pooledRate = (controlRate * controlN + treatmentRate * treatmentN) / (controlN + treatmentN);
const se = Math.sqrt(pooledRate * (1 - pooledRate) * (1/controlN + 1/treatmentN));
const zScore = se > 0 ? ((treatmentRate - controlRate) / se).toFixed(4) : 0;
const pValue = (Math.abs(zScore) > 1.96) ? 0.01 : (Math.abs(zScore) > 1.645 ? 0.05 : 0.15);
const isSignificant = pValue < 0.05 ? 'true' : 'false';
const confidenceLevel = pValue < 0.01 ? '99%' : pValue < 0.05 ? '95%' : 'Not significant';

// Winner determination
const winner = treatmentRate > controlRate && isSignificant === 'true' ? 'treatment' : controlRate > treatmentRate && isSignificant === 'true' ? 'control' : 'inconclusive';

return {
  control_rate: controlRate,
  treatment_rate: treatmentRate,
  relative_lift: relativeLift,
  p_value: pValue,
  z_score: zScore,
  is_significant: isSignificant,
  confidence_level: confidenceLevel,
  winner: winner,
  total_sample_size: controlN + treatmentN,
};`,
          inputVariables: [
            { name: "variant_data", type: "string" },
            { name: "test_metrics", type: "string" },
          ],
          outputVariables: [
            { name: "control_rate", type: "number" },
            { name: "treatment_rate", type: "number" },
            { name: "relative_lift", type: "string" },
            { name: "p_value", type: "number" },
            { name: "is_significant", type: "string" },
            { name: "confidence_level", type: "string" },
            { name: "winner", type: "string" },
            { name: "total_sample_size", type: "number" },
          ],
        },
      }),

      // 4. Significance condition
      n("4", "condition", "Test reached statistical significance?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_significant", operator: "equals", value: "true" },
          ],
        },
      }),

      // 5. AI business interpretation
      aiNode(
        "5",
        "AI generate business interpretation",
        1000,
        "You are a growth analyst. Interpret these A/B test results for business stakeholders. Explain what happened, why it matters for the business, recommended action (roll out winner, extend test, new hypothesis), and potential revenue impact. Avoid jargon. Return JSON: {interpretation: string, recommendation: string, revenue_impact_estimate: string, next_steps: [string], experiment_ideas: [{name, hypothesis, expected_lift}], risk_assessment: string}",
        "Test: {test_name}\\nControl rate: {control_rate}\\nTreatment rate: {treatment_rate}\\nRelative lift: {relative_lift}%\\nP-value: {p_value}\\nConfidence: {confidence_level}\\nWinner: {winner}\\nTotal samples: {total_sample_size}",
        [
          {
            field: "interpretation",
            as: "business_interpretation",
            type: "string",
          },
          { field: "recommendation", as: "recommendation", type: "string" },
          {
            field: "revenue_impact_estimate",
            as: "revenue_impact",
            type: "string",
          },
          { field: "next_steps", as: "next_steps", type: "string" },
          { field: "experiment_ideas", as: "experiment_ideas", type: "string" },
        ],
      ),

      // 6. Test registry update
      n("6", "hitApi", "Update test registry in Google Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/TestRegistry!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{test_name}","{control_rate}","{treatment_rate}","{relative_lift}","{p_value}","{confidence_level}","{winner}","{total_sample_size}","{recommendation}","{revenue_impact}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "registry_updated",
              type: "number",
            },
          ],
        },
      }),

      // 7. Growth team notification
      slackNode(
        "7",
        "Notify growth team of test results",
        1480,
        "A/B Test Results: {test_name}\\n\\nWinner: {winner} | Confidence: {confidence_level}\\nControl: {control_rate} vs Treatment: {treatment_rate}\\nLift: {relative_lift}% | P-value: {p_value}\\n\\n{business_interpretation}\\n\\nRecommendation: {recommendation}\\nRevenue impact: {revenue_impact}",
      ),

      // 8. Follow-on experiment ideas
      slackNode(
        "8",
        "Share follow-on experiment ideas",
        1720,
        "Experiment Ideas from {test_name} analysis:\\n\\n{experiment_ideas}\\n\\nNext steps: {next_steps}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 269. BRANDED VS NON-BRANDED ANALYSIS (7 nodes)
  // ===================================================================

  "Branded vs Non-Branded Search Analysis": {
    summary:
      "Runs weekly to pull Google Ads search term data, uses AI to classify terms as branded or non-branded, aggregates spend and performance metrics, detects imbalances, updates a tracking dashboard in Sheets, and sends budget recommendations to Slack.",
    nodes: [
      trigger("Weekly brand analysis schedule", "schedule", {
        schedule: "Every Monday at 8:00 AM UTC",
        howItStarts:
          "Runs weekly to analyze the split between branded and non-branded search traffic and spend in Google Ads",
        setup:
          "Configure Google Ads API credentials with reporting access. Set the customer ID and date range. Create a Google Sheet for the brand analysis dashboard.",
        inputFields: [],
      }),

      // 2. Google Ads search term pull
      n("2", "hitApi", "Google Ads API: Pull search term report", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_developer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT search_term_view.search_term, metrics.impressions, metrics.clicks, metrics.cost_micros, metrics.conversions, metrics.conversions_value FROM search_term_view WHERE segments.date DURING LAST_7_DAYS ORDER BY metrics.cost_micros DESC LIMIT 500",
            },
          ],
          variables: [
            { name: "results", customName: "search_term_data", type: "string" },
          ],
        },
      }),

      // 3. AI brand classification
      aiNode(
        "3",
        "AI classify branded vs non-branded terms",
        520,
        "You are a PPC analyst. Classify each search term as branded (contains company name, product names, or close misspellings) or non_branded (generic/competitor terms). Also tag: competitor (mentions competitor brands), high_intent (purchase/pricing/demo/trial), informational (how-to/what-is). Return JSON: {classifications: [{term, type, intent_category, spend, clicks, conversions}], branded_count: number, non_branded_count: number, competitor_count: number, branded_spend: number, non_branded_spend: number, branded_conversions: number, non_branded_conversions: number}",
        "Brand names to match: {brand_names}\\nSearch term data:\\n{search_term_data}",
        [
          {
            field: "classifications",
            as: "term_classifications",
            type: "string",
          },
          { field: "branded_count", as: "branded_count", type: "number" },
          {
            field: "non_branded_count",
            as: "non_branded_count",
            type: "number",
          },
          { field: "branded_spend", as: "branded_spend", type: "number" },
          {
            field: "non_branded_spend",
            as: "non_branded_spend",
            type: "number",
          },
          {
            field: "branded_conversions",
            as: "branded_conversions",
            type: "number",
          },
          {
            field: "non_branded_conversions",
            as: "non_branded_conversions",
            type: "number",
          },
        ],
      ),

      // 4. Spend aggregation
      n("4", "runCode", "Aggregate spend ratios and detect imbalance", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Calculate branded vs non-branded ratios and detect imbalances
const brandedSpend = parseFloat(inputs.branded_spend) || 0;
const nonBrandedSpend = parseFloat(inputs.non_branded_spend) || 0;
const totalSpend = brandedSpend + nonBrandedSpend;
const brandedConversions = parseFloat(inputs.branded_conversions) || 0;
const nonBrandedConversions = parseFloat(inputs.non_branded_conversions) || 0;

const brandedPct = totalSpend > 0 ? Math.round(brandedSpend / totalSpend * 100) : 0;
const nonBrandedPct = 100 - brandedPct;

// CPA calculations
const brandedCPA = brandedConversions > 0 ? (brandedSpend / brandedConversions).toFixed(2) : 'N/A';
const nonBrandedCPA = nonBrandedConversions > 0 ? (nonBrandedSpend / nonBrandedConversions).toFixed(2) : 'N/A';

// Imbalance detection: branded should be < 40% of total spend
const isImbalanced = brandedPct > 40 ? 'true' : 'false';
const imbalanceDescription = isImbalanced === 'true'
  ? 'Branded spend is ' + brandedPct + '% of total — above 40% threshold. Consider shifting budget to non-branded acquisition.'
  : 'Spend split is healthy: ' + brandedPct + '% branded / ' + nonBrandedPct + '% non-branded.';

return {
  branded_spend_pct: brandedPct,
  non_branded_spend_pct: nonBrandedPct,
  total_spend: totalSpend.toFixed(2),
  branded_cpa: brandedCPA,
  non_branded_cpa: nonBrandedCPA,
  is_imbalanced: isImbalanced,
  imbalance_description: imbalanceDescription,
};`,
          inputVariables: [
            { name: "branded_spend", type: "number" },
            { name: "non_branded_spend", type: "number" },
            { name: "branded_conversions", type: "number" },
            { name: "non_branded_conversions", type: "number" },
          ],
          outputVariables: [
            { name: "branded_spend_pct", type: "number" },
            { name: "non_branded_spend_pct", type: "number" },
            { name: "total_spend", type: "string" },
            { name: "branded_cpa", type: "string" },
            { name: "non_branded_cpa", type: "string" },
            { name: "is_imbalanced", type: "string" },
            { name: "imbalance_description", type: "string" },
          ],
        },
      }),

      // 5. Imbalance condition
      n("5", "condition", "Brand spend imbalance detected?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "is_imbalanced", operator: "equals", value: "true" },
          ],
        },
      }),

      // 6. Dashboard update
      n("6", "hitApi", "Update brand analysis dashboard in Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/BrandAnalysis!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{branded_spend_pct}","{non_branded_spend_pct}","{total_spend}","{branded_cpa}","{non_branded_cpa}","{branded_count}","{non_branded_count}","{is_imbalanced}","{imbalance_description}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "dashboard_updated",
              type: "number",
            },
          ],
        },
      }),

      // 7. Budget recommendation
      slackNode(
        "7",
        "Send budget recommendation to paid team",
        1480,
        "Weekly Brand vs Non-Brand Report\\n\\nSpend split: {branded_spend_pct}% branded / {non_branded_spend_pct}% non-branded\\nTotal spend: ${total_spend}\\nBranded CPA: ${branded_cpa} | Non-branded CPA: ${non_branded_cpa}\\nBranded terms: {branded_count} | Non-branded: {non_branded_count}\\n\\n{imbalance_description}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 270. CHURN PREDICTION MODEL RUNNER (8 nodes)
  // ===================================================================

  "Churn Prediction Model Runner": {
    summary:
      "Runs on a customer cohort schedule, assembles multi-signal features (usage, billing, support), scores churn risk with AI, classifies into risk tiers, updates CRM records in HubSpot, builds a high-risk dashboard in Sheets, alerts CS via Slack, and logs outcomes for model backtesting.",
    nodes: [
      trigger("Weekly customer cohort analysis", "schedule", {
        schedule: "Every Monday at 7:00 AM UTC",
        howItStarts:
          "Runs weekly to score all active customers for churn risk based on multi-signal behavioral analysis",
        setup:
          "Configure API access to: product usage database, billing system (Stripe), and support platform (Zendesk). Set risk tier thresholds in the classification node. Connect HubSpot for CRM updates.",
        inputFields: [],
      }),

      // 2. Multi-signal feature assembly
      n("2", "hitApi", "Pull customer usage and engagement signals", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.{analytics_platform}/v1/cohort/features",
          headers: [
            { key: "Authorization", value: "Bearer {analytics_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "cohort", value: "active_customers" },
            {
              key: "features",
              value:
                '["login_frequency_30d","feature_adoption_score","support_tickets_30d","billing_issues_90d","nps_score","days_since_last_login","contract_renewal_days","usage_trend_slope","team_size_change","integration_count"]',
            },
            { key: "period", value: "last_90_days" },
          ],
          variables: [
            {
              name: "customers",
              customName: "customer_features",
              type: "string",
            },
            { name: "total_count", customName: "cohort_size", type: "number" },
          ],
        },
      }),

      // 3. AI churn scoring
      aiNode(
        "3",
        "AI score churn risk for each customer",
        520,
        "You are a customer success data scientist. Score each customer for churn risk (0-100) based on behavioral signals. Weight factors: declining login frequency (high weight), low feature adoption (high), recent support escalations (medium), billing issues (high), low NPS (medium), long time since last login (high), approaching renewal (medium). Also identify the top 3 risk drivers per customer. Return JSON: {scored_customers: [{id, name, churn_score, risk_tier, top_drivers: [string], recommended_action: string}], high_risk_count: number, medium_risk_count: number, low_risk_count: number, average_score: number}",
        "Customer features ({cohort_size} customers):\\n{customer_features}",
        [
          { field: "scored_customers", as: "scored_customers", type: "string" },
          { field: "high_risk_count", as: "high_risk_count", type: "number" },
          {
            field: "medium_risk_count",
            as: "medium_risk_count",
            type: "number",
          },
          { field: "low_risk_count", as: "low_risk_count", type: "number" },
          { field: "average_score", as: "average_churn_score", type: "number" },
        ],
      ),

      // 4. Risk tier classification condition
      n("4", "condition", "Any high-risk customers detected?", 760, {
        conditionDetails: {
          conditions: [
            { field: "high_risk_count", operator: "greater_than", value: "0" },
          ],
        },
      }),

      // 5. CRM update in HubSpot
      n("5", "hitApi", "Update churn scores in HubSpot CRM", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/batch/update",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "inputs", value: "{scored_customers}" }],
          variables: [
            { name: "status", customName: "crm_update_status", type: "string" },
            {
              name: "results",
              customName: "crm_updated_contacts",
              type: "string",
            },
          ],
        },
      }),

      // 6. High-risk dashboard in Sheets
      n("6", "hitApi", "Update high-risk dashboard in Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ChurnRisk!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{cohort_size}","{high_risk_count}","{medium_risk_count}","{low_risk_count}","{average_churn_score}","{scored_customers}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "dashboard_updated",
              type: "number",
            },
          ],
        },
      }),

      // 7. CS Slack alert
      slackNode(
        "7",
        "Alert CS team about high-risk accounts",
        1480,
        "Churn Risk Alert - Weekly Report\\n\\nCohort: {cohort_size} active customers\\nHigh risk: {high_risk_count} | Medium risk: {medium_risk_count} | Low risk: {low_risk_count}\\nAverage churn score: {average_churn_score}/100\\n\\nHigh-risk accounts requiring immediate attention:\\n{scored_customers}\\n\\nCRM records updated. Dashboard refreshed.",
      ),

      // 8. Outcome logging for backtesting
      n("8", "hitApi", "Log predictions for model backtesting", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ChurnBacktest!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{cohort_size}","{high_risk_count}","{average_churn_score}","{scored_customers}","pending_validation"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "backtest_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 271. COMBINED GOOGLE + META REPORT (8 nodes)
  // ===================================================================

  "Combined Google + Meta Ads Report": {
    summary:
      "Runs weekly to pull metrics from both Google Ads and Meta Ads, normalizes schemas into a unified format, generates AI cross-platform insights, detects pacing breaches, updates a consolidated dashboard in Sheets, and sends a budget shift digest via email.",
    nodes: [
      trigger("Weekly cross-platform report schedule", "schedule", {
        schedule: "Every Monday at 9:00 AM UTC",
        howItStarts:
          "Runs weekly to generate a unified performance report combining Google Ads and Meta Ads data",
        setup:
          "Configure Google Ads API credentials (customer ID, developer token) and Meta Marketing API credentials (ad account ID, access token). Create a Google Sheet with columns for the unified dashboard.",
        inputFields: [],
      }),

      // 2. Google Ads metrics pull
      n("2", "hitApi", "Google Ads API: Pull weekly metrics", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_developer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, metrics.impressions, metrics.clicks, metrics.cost_micros, metrics.conversions, metrics.conversions_value, campaign.bidding_strategy_type FROM campaign WHERE segments.date DURING LAST_7_DAYS AND campaign.status = 'ENABLED'",
            },
          ],
          variables: [
            { name: "results", customName: "google_ads_data", type: "string" },
          ],
        },
      }),

      // 3. Meta Ads metrics pull
      n(
        "3",
        "hitApi",
        "Meta Ads API: Pull weekly metrics",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://graph.facebook.com/v18.0/act_{meta_ad_account_id}/insights?fields=campaign_name,impressions,clicks,spend,actions,action_values&date_preset=last_7d&level=campaign&limit=100",
            headers: [
              { key: "Authorization", value: "Bearer {meta_access_token}" },
            ],
            body: [],
            variables: [
              { name: "data", customName: "meta_ads_data", type: "string" },
            ],
          },
        },
        460,
      ),

      // 4. Schema normalization
      n("4", "runCode", "Normalize Google and Meta into unified schema", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Normalize Google Ads and Meta Ads data into a unified schema
const googleData = JSON.parse(inputs.google_ads_data || '[]');
const metaData = JSON.parse(inputs.meta_ads_data || '[]');

// Normalize Google Ads (cost_micros to dollars)
const googleNormalized = googleData.map(row => {
  const campaign = row.campaign || {};
  const metrics = row.metrics || {};
  return {
    platform: 'Google Ads',
    campaign_name: campaign.name || 'Unknown',
    impressions: parseInt(metrics.impressions || 0),
    clicks: parseInt(metrics.clicks || 0),
    spend: (parseInt(metrics.cost_micros || 0) / 1000000).toFixed(2),
    conversions: parseFloat(metrics.conversions || 0),
    revenue: parseFloat(metrics.conversions_value || 0),
  };
});

// Normalize Meta Ads
const metaNormalized = metaData.map(row => {
  const conversions = (row.actions || []).find(a => a.action_type === 'purchase');
  const revenue = (row.action_values || []).find(a => a.action_type === 'purchase');
  return {
    platform: 'Meta Ads',
    campaign_name: row.campaign_name || 'Unknown',
    impressions: parseInt(row.impressions || 0),
    clicks: parseInt(row.clicks || 0),
    spend: parseFloat(row.spend || 0).toFixed(2),
    conversions: conversions ? parseFloat(conversions.value) : 0,
    revenue: revenue ? parseFloat(revenue.value) : 0,
  };
});

const unified = [...googleNormalized, ...metaNormalized];
const googleSpend = googleNormalized.reduce((s, r) => s + parseFloat(r.spend), 0);
const metaSpend = metaNormalized.reduce((s, r) => s + parseFloat(r.spend), 0);
const totalSpend = googleSpend + metaSpend;
const googleRoas = googleSpend > 0 ? (googleNormalized.reduce((s, r) => s + r.revenue, 0) / googleSpend).toFixed(2) : '0';
const metaRoas = metaSpend > 0 ? (metaNormalized.reduce((s, r) => s + r.revenue, 0) / metaSpend).toFixed(2) : '0';

return {
  unified_data: JSON.stringify(unified),
  google_spend: googleSpend.toFixed(2),
  meta_spend: metaSpend.toFixed(2),
  total_spend: totalSpend.toFixed(2),
  google_roas: googleRoas,
  meta_roas: metaRoas,
  google_campaign_count: googleNormalized.length,
  meta_campaign_count: metaNormalized.length,
};`,
          inputVariables: [
            { name: "google_ads_data", type: "string" },
            { name: "meta_ads_data", type: "string" },
          ],
          outputVariables: [
            { name: "unified_data", type: "string" },
            { name: "google_spend", type: "string" },
            { name: "meta_spend", type: "string" },
            { name: "total_spend", type: "string" },
            { name: "google_roas", type: "string" },
            { name: "meta_roas", type: "string" },
            { name: "google_campaign_count", type: "number" },
            { name: "meta_campaign_count", type: "number" },
          ],
        },
      }),

      // 5. AI cross-platform insights
      aiNode(
        "5",
        "AI generate cross-platform insights",
        760,
        "You are a performance marketing analyst. Compare Google Ads vs Meta Ads performance. Identify: which platform is more efficient (ROAS, CPA), platform-specific trends, budget allocation recommendations, cross-channel attribution considerations, and specific campaigns to pause/scale. Return JSON: {executive_summary: string, platform_comparison: string, top_performing_campaigns: [string], underperforming_campaigns: [string], budget_recommendation: string, reallocation_amount: string, weekly_trend: string, pacing_status: string}",
        "Google Ads: {google_campaign_count} campaigns, ${google_spend} spend, {google_roas}x ROAS\\nMeta Ads: {meta_campaign_count} campaigns, ${meta_spend} spend, {meta_roas}x ROAS\\nTotal spend: ${total_spend}\\n\\nUnified data:\\n{unified_data}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          {
            field: "platform_comparison",
            as: "platform_comparison",
            type: "string",
          },
          {
            field: "budget_recommendation",
            as: "budget_recommendation",
            type: "string",
          },
          {
            field: "reallocation_amount",
            as: "reallocation_amount",
            type: "string",
          },
          { field: "pacing_status", as: "pacing_status", type: "string" },
        ],
      ),

      // 6. Pacing breach condition
      n("6", "condition", "Budget pacing breach detected?", 1000, {
        conditionDetails: {
          conditions: [
            {
              field: "pacing_status",
              operator: "not_equals",
              value: "on_track",
            },
          ],
        },
      }),

      // 7. Dashboard update
      n("7", "hitApi", "Update cross-platform dashboard in Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/CrossPlatform!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{google_spend}","{meta_spend}","{total_spend}","{google_roas}","{meta_roas}","{google_campaign_count}","{meta_campaign_count}","{pacing_status}","{budget_recommendation}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "dashboard_updated",
              type: "number",
            },
          ],
        },
      }),

      // 8. Budget shift digest email
      emailNode(
        "8",
        "Send budget shift digest to marketing",
        1480,
        "{marketing_team_email}",
        "Weekly Cross-Platform Report: ${total_spend} spent | Pacing: {pacing_status}",
        "Cross-Platform Performance Report\\n\\nGoogle Ads: ${google_spend} spent | {google_roas}x ROAS | {google_campaign_count} campaigns\\nMeta Ads: ${meta_spend} spent | {meta_roas}x ROAS | {meta_campaign_count} campaigns\\nTotal: ${total_spend}\\n\\nPacing: {pacing_status}\\n\\n{executive_summary}\\n\\nPlatform comparison: {platform_comparison}\\n\\nBudget recommendation: {budget_recommendation}\\nSuggested reallocation: {reallocation_amount}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("1", "3"),
      e("2", "4"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 272. COMPETITOR SOCIAL WATCH (7 nodes)
  // ===================================================================

  "Competitor Social Watch": {
    summary:
      "Runs daily to pull competitor social media posts, normalizes engagement metrics across platforms, uses AI to analyze themes, cadence, and strategy patterns, detects anomalies (viral content, strategy shifts), sends strategy notifications to Slack, and archives findings to a tracking sheet.",
    nodes: [
      trigger("Daily competitor social monitoring", "schedule", {
        schedule: "Every day at 8:00 AM UTC",
        howItStarts:
          "Runs daily to monitor competitor social media activity across platforms and detect strategic shifts",
        setup:
          "Configure social listening API credentials (Brandwatch, Sprout Social, or direct platform APIs). Set competitor brand names and social handles in the pull node. Create a tracking spreadsheet.",
        inputFields: [],
      }),

      // 2. Competitor social posts pull
      n("2", "hitApi", "Pull competitor social posts via social API", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.{social_listening_tool}/v2/mentions/search",
          headers: [
            { key: "Authorization", value: "Bearer {social_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                '{"authors":"{competitor_handles}","date_range":"last_24h","platforms":["twitter","linkedin","instagram","facebook"],"limit":100}',
            },
          ],
          variables: [
            { name: "data", customName: "competitor_posts", type: "string" },
            { name: "meta.total", customName: "total_posts", type: "number" },
          ],
        },
      }),

      // 3. Engagement normalization
      n("3", "runCode", "Normalize engagement metrics across platforms", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Normalize engagement metrics across different social platforms
const posts = JSON.parse(inputs.competitor_posts || '[]');

const normalized = posts.map(post => {
  const platform = post.platform || 'unknown';
  const likes = post.likes || post.favorite_count || 0;
  const shares = post.shares || post.retweet_count || post.reposts || 0;
  const comments = post.comments || post.reply_count || 0;
  const impressions = post.impressions || post.reach || 0;

  // Normalize engagement rate per platform (different baselines)
  const totalEngagement = likes + shares * 2 + comments * 3; // Weight shares and comments higher
  const engagementRate = impressions > 0 ? ((totalEngagement / impressions) * 100).toFixed(2) : 0;

  return {
    competitor: post.author || post.handle || '',
    platform: platform,
    text: (post.text || post.content || '').substring(0, 500),
    likes, shares, comments, impressions,
    engagement_rate: parseFloat(engagementRate),
    posted_at: post.created_at || post.date || '',
    url: post.url || post.permalink || '',
    has_media: post.media ? true : false,
    content_type: post.media ? 'visual' : 'text',
  };
});

// Detect anomalies (posts with engagement 3x above average)
const avgEngagement = normalized.reduce((s, p) => s + p.engagement_rate, 0) / (normalized.length || 1);
const anomalies = normalized.filter(p => p.engagement_rate > avgEngagement * 3);
const hasAnomalies = anomalies.length > 0 ? 'true' : 'false';
const postsByCompetitor = {};
normalized.forEach(p => { postsByCompetitor[p.competitor] = (postsByCompetitor[p.competitor] || 0) + 1; });

return {
  normalized_posts: JSON.stringify(normalized),
  average_engagement_rate: avgEngagement.toFixed(2),
  anomaly_posts: JSON.stringify(anomalies),
  anomaly_count: anomalies.length,
  has_anomalies: hasAnomalies,
  posts_by_competitor: JSON.stringify(postsByCompetitor),
  total_normalized: normalized.length,
};`,
          inputVariables: [{ name: "competitor_posts", type: "string" }],
          outputVariables: [
            { name: "normalized_posts", type: "string" },
            { name: "average_engagement_rate", type: "string" },
            { name: "anomaly_posts", type: "string" },
            { name: "anomaly_count", type: "number" },
            { name: "has_anomalies", type: "string" },
            { name: "posts_by_competitor", type: "string" },
            { name: "total_normalized", type: "number" },
          ],
        },
      }),

      // 4. AI theme/cadence analysis
      aiNode(
        "4",
        "AI analyze themes, cadence, and strategy",
        760,
        "You are a competitive intelligence analyst. Analyze competitor social posts for: content themes (product launches, thought leadership, promotional, hiring, partnerships, culture), posting cadence (frequency, timing patterns), engagement patterns (what content types perform best), strategic signals (new positioning, market expansion, product pivots), and sentiment of audience response. Return JSON: {themes: [{competitor, theme, frequency, avg_engagement}], cadence_analysis: string, top_performing_content: [string], strategic_signals: [string], recommendations: [string], threat_level: string, summary: string}",
        "Competitor posts ({total_normalized} posts, avg engagement: {average_engagement_rate}%):\\n{normalized_posts}\\nPosts by competitor: {posts_by_competitor}\\nAnomalies: {anomaly_posts}",
        [
          { field: "themes", as: "content_themes", type: "string" },
          { field: "cadence_analysis", as: "cadence_analysis", type: "string" },
          {
            field: "strategic_signals",
            as: "strategic_signals",
            type: "string",
          },
          {
            field: "recommendations",
            as: "strategy_recommendations",
            type: "string",
          },
          { field: "threat_level", as: "threat_level", type: "string" },
          { field: "summary", as: "analysis_summary", type: "string" },
        ],
      ),

      // 5. Anomaly condition
      n("5", "condition", "Anomalous competitor activity detected?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "has_anomalies", operator: "equals", value: "true" },
          ],
        },
      }),

      // 6. Strategy Slack notification
      slackNode(
        "6",
        "Notify strategy team about competitor intel",
        1240,
        "Daily Competitor Social Intelligence\\n\\nPosts analyzed: {total_normalized} | Avg engagement: {average_engagement_rate}%\\nAnomalies: {anomaly_count} | Threat level: {threat_level}\\n\\n{analysis_summary}\\n\\nStrategic signals: {strategic_signals}\\n\\nCadence: {cadence_analysis}\\n\\nRecommendations: {strategy_recommendations}",
      ),

      // 7. Tracker archive
      n("7", "hitApi", "Archive competitor intel to tracking sheet", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/CompetitorWatch!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{date}","{total_normalized}","{average_engagement_rate}","{anomaly_count}","{threat_level}","{content_themes}","{strategic_signals}","{strategy_recommendations}","{analysis_summary}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_archived",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },
};
